import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomLayout } from '@/components/ui/custom-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, Linkedin, Building2, Briefcase, Target, BarChart3, GraduationCap, Heart, Users, X, Paperclip, ChevronDown, ChevronRight, Settings, CheckCircle, Mail, ArrowLeft, TrendingUp, Loader2, MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { templateMetadata, sampleResumeData, Template1, Template2, Template3, Template4, Template5, Template6, Template7, Template8, Template9, Template10 } from '@/components/ResumeTemplates';
import { useAuth } from '@/hooks/useAuth';
import { ResumeService } from '@/services/resumeService';
import { TailoredResumeService } from '@/services/tailoredResumeService';
import { ResumeLibraryService } from '@/services/resumeLibraryService';
import { supabase } from '@/integrations/supabase/client';
import { JobWizard } from '@/components/JobWizard/JobWizard';
import { DocumentUpload } from '@/components/DocumentUpload';
import { JobAnalysisService, type JobAnalysisData, type SavedJobAnalysis } from "@/services/jobAnalysisService";
import { Step1Responsibilities } from "@/components/AdvancedTailoringWizard/Step1Responsibilities";
import { Step2Requirements } from "@/components/AdvancedTailoringWizard/Step2Requirements";
import { Step3Skills } from "@/components/AdvancedTailoringWizard/Step3Skills";
import { convertPDFToText } from "@/services/pdfConversionService";
import { TailoringDebugViewer } from '@/components/TailoringDebugViewer';
function ResumeTailoringTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null);
  const {
    toast
  } = useToast();
  const {
    user,
    loading
  } = useAuth();
  const [basicOption, setBasicOption] = useState('');
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [selectedUploadMethod, setSelectedUploadMethod] = useState('');
  const [selectedLinkedInMethod, setSelectedLinkedInMethod] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pastedResumeText, setPastedResumeText] = useState('');
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [showJobWizard, setShowJobWizard] = useState(false);
  const [showResumeDropdown, setShowResumeDropdown] = useState(true);
  const [showJobDropdown, setShowJobDropdown] = useState(true);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [processingStep1, setProcessingStep1] = useState(false);
  const [step1Complete, setStep1Complete] = useState(false);
  const [processedResumeData, setProcessedResumeData] = useState<string>('');
  const [tailoringSource, setTailoringSource] = useState(''); // No default selection
  const [revisionInstructions, setRevisionInstructions] = useState('');
  const [revisionDocument, setRevisionDocument] = useState<File | null>(null);
  const [selectedRevisionOptions, setSelectedRevisionOptions] = useState<string[]>([]);
  const [generateAnalysis, setGenerateAnalysis] = useState(true); // Default to true
  const [jobDescription, setJobDescription] = useState('');
  const [jobPositionTitle, setJobPositionTitle] = useState('');
  const [jobPositionIndustry, setJobPositionIndustry] = useState('');
  const [careerLevel, setCareerLevel] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [missionAlignment, setMissionAlignment] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [linkedInDataFile, setLinkedInDataFile] = useState<File | null>(null);
  const [additionalDocuments, setAdditionalDocuments] = useState<{
    file: File;
    extractedText: string;
  }[]>([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showAdvancedOptionsJob, setShowAdvancedOptionsJob] = useState(false);
  const [showAdvancedOptionsInstitution, setShowAdvancedOptionsInstitution] = useState(false);
  const [institutionName, setInstitutionName] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>(['resume']);
  const [step3Completed, setStep3Completed] = useState(false); // Explicit step 3 completion
  const [step4Completed, setStep4Completed] = useState(false); // Job description/details completion
  const [additionalInstructionsAnswer, setAdditionalInstructionsAnswer] = useState<'yes' | 'no' | ''>(''); // Step 5 answer
  const [step5Completed, setStep5Completed] = useState(false); // Additional instructions question answered
  const [additionalInstructionsText, setAdditionalInstructionsText] = useState(''); // Step 6 content
  const [additionalInstructionsDocuments, setAdditionalInstructionsDocuments] = useState<File[]>([]); // Step 6 documents
  const [step6Completed, setStep6Completed] = useState(false); // Additional content completion
  const [jobDataReady, setJobDataReady] = useState(false);
  const [processedJobData, setProcessedJobData] = useState({
    title: '',
    description: ''
  });

  // Advanced options state
  const [mentionCompanyName, setMentionCompanyName] = useState(false);
  const [mentionCompanyMission, setMentionCompanyMission] = useState(false);
  const [mentionEducation, setMentionEducation] = useState(false);
  const [featureSolutionAsset, setFeatureSolutionAsset] = useState(false);
  const [workExperienceFramework, setWorkExperienceFramework] = useState('none');
  const [transferableSkillsFocus, setTransferableSkillsFocus] = useState(false);
  const [skillRelevanceOptimization, setSkillRelevanceOptimization] = useState(false);
  const [irrelevantJobFiltering, setIrrelevantJobFiltering] = useState(false);
  const [selectedTemplateMain, setSelectedTemplateMain] = useState('');
  const [sectionsToTailor, setSectionsToTailor] = useState<string[]>(['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline']);
  const [careerTransitionOptions, setCareerTransitionOptions] = useState<string[]>([]);
  const [quantificationLevel, setQuantificationLevel] = useState('');
  const [writingStyle, setWritingStyle] = useState('chronological');
  const [brandingStyle, setBrandingStyle] = useState('');
  const [selectedTailoringType, setSelectedTailoringType] = useState('basic');
  const [showAdvancedWizard, setShowAdvancedWizard] = useState(false);
  const [jobAnalysis, setJobAnalysis] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState<string[]>([]);
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  // Advanced wizard state
  const [wizardStep, setWizardStep] = useState<'responsibilities' | 'requirements' | 'skills'>('responsibilities');
  const [savedJobAnalyses, setSavedJobAnalyses] = useState<SavedJobAnalysis[]>([]);
  const [loadingSavedAnalyses, setLoadingSavedAnalyses] = useState(false);
  const [selectedSavedAnalysisId, setSelectedSavedAnalysisId] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);

  // Assessment questions state
  const [assessmentQuestions, setAssessmentQuestions] = useState('');
  const [selectedAssessmentMethod, setSelectedAssessmentMethod] = useState('');
  const [solutionAssetInSummary, setSolutionAssetInSummary] = useState(false);

  // Debug data state - load asynchronously to prevent blocking
  const [debugData, setDebugData] = useState<any[]>([]);

  // Step management state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [stepExpanded, setStepExpanded] = useState<Record<number, boolean>>({
    1: true,
    // Start with step 1 expanded only if not completed
    2: false,
    3: false,
    4: false,
    5: false,
    6: false
  });

  // Load debug data asynchronously
  React.useEffect(() => {
    const loadDebugData = async () => {
      try {
        const stored = localStorage.getItem('tailoring-debug-data');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Only keep last 10 entries to prevent memory issues
          const limited = parsed.slice(0, 10);
          setDebugData(limited);
        }
      } catch (error) {
        console.log('Error loading debug data:', error);
        setDebugData([]);
      }
    };
    loadDebugData();
  }, []);

  // Step management functions - Flow-based progress tracking
  const getStepProgress = () => {
    // Progress based on 5 main sequential steps (1-5), Steps 6-7 are optional/conditional
    const mainStepsCompleted = completedSteps.filter(step => step <= 5).length;
    return Math.round(mainStepsCompleted / 5 * 100); // 5 main steps (0%, 20%, 40%, 60%, 80%, 100%)
  };
  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return step1Complete && !!processedResumeData;
      case 2:
        return !!tailoringSource;
      // Just having a selection is enough for step 2
      case 3:
        return step3Completed;
      // Requires explicit completion button click
      case 4:
        return step4Completed;
      // Job description/details completion
      case 5:
        return step5Completed;
      // Additional instructions question answered
      case 6:
        return step6Completed;
      // Additional content completion (only relevant if step 5 = yes)
      default:
        return false;
    }
  };

  // Auto-expand appropriate step when tailoring source is selected
  React.useEffect(() => {
    if (!tailoringSource) return;
    const newExpanded = {
      ...stepExpanded
    };
    let hasChanges = false;

    // Auto-expand the appropriate step based on tailoring source
    if (tailoringSource === 'job-description' && !stepExpanded[4] && !isStepComplete(4)) {
      newExpanded[4] = true;
      hasChanges = true;
    } else if (tailoringSource === 'assessment-questions' && !stepExpanded[4] && !isStepComplete(4)) {
      newExpanded[4] = true;
      hasChanges = true;
    } else if (tailoringSource === 'revisions' && !stepExpanded[3] && !isStepComplete(3)) {
      newExpanded[3] = true;
      hasChanges = true;
    }
    if (hasChanges) {
      setStepExpanded(newExpanded);
    }
  }, [tailoringSource]);

  // Auto-collapse completed steps and auto-expand next step
  React.useEffect(() => {
    const newExpanded = {
      ...stepExpanded
    };
    let hasChanges = false;

    // Handle steps 1-5 (main flow)
    for (let step = 1; step <= 5; step++) {
      if (isStepComplete(step) && stepExpanded[step] === true) {
        newExpanded[step] = false; // Auto-collapse completed steps
        hasChanges = true;

        // Special logic for step 5 (additional instructions question)
        if (step === 5 && additionalInstructionsAnswer === 'yes') {
          // If user said YES, expand step 6
          if (!stepExpanded[6]) {
            newExpanded[6] = true;
            hasChanges = true;
          }
        } else if (step === 5 && additionalInstructionsAnswer === 'no') {
          // If user said NO, we're done with the main flow
          // No additional steps to show
        } else {
          // Auto-expand next step if it exists and isn't completed
          const nextStep = step + 1;
          if (nextStep <= 5 && !isStepComplete(nextStep) && !stepExpanded[nextStep]) {
            newExpanded[nextStep] = true;
            hasChanges = true;
          }
        }
      }
    }

    // Handle step 6 completion - end of flow
    if (isStepComplete(6) && stepExpanded[6] === true) {
      newExpanded[6] = false;
      hasChanges = true;
    }
    if (hasChanges) {
      setStepExpanded(newExpanded);
    }
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, additionalInstructionsAnswer, step6Completed]);

  // Enhanced step completion handlers that force immediate progress updates
  const markStepComplete = (step: number, completed: boolean = true) => {
    switch (step) {
      case 3:
        setStep3Completed(completed);
        break;
      case 4:
        setStep4Completed(completed);
        break;
      case 5:
        setStep5Completed(completed);
        break;
      case 6:
        setStep6Completed(completed);
        break;
    }
  };
  const toggleStepExpansion = (step: number) => {
    setStepExpanded(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
  };
  const canProceedToStep = (step: number): boolean => {
    if (step === 1) return true;
    return isStepComplete(step - 1);
  };
  const goToStep = (step: number) => {
    if (canProceedToStep(step)) {
      setCurrentStep(step as 1 | 2 | 3 | 4 | 5 | 6);
      setStepExpanded(prev => ({
        ...prev,
        [step]: true
      }));
    }
  };
  const continueToNextStep = () => {
    const nextStep = currentStep + 1;
    if (nextStep <= 6 && isStepComplete(currentStep)) {
      // Mark current step as complete
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }

      // Collapse current step and expand next step
      setStepExpanded(prev => ({
        ...prev,
        [currentStep]: false,
        [nextStep]: true
      }));
      setCurrentStep(nextStep as 1 | 2 | 3 | 4 | 5 | 6);
    }
  };

  // Update completedSteps when steps are actually completed
  React.useEffect(() => {
    const newCompletedSteps: number[] = [];
    for (let i = 1; i <= 6; i++) {
      if (isStepComplete(i)) {
        newCompletedSteps.push(i);
      }
    }
    setCompletedSteps(newCompletedSteps);
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, step6Completed]);

  // Automatically advance currentStep to next incomplete step
  React.useEffect(() => {
    // Find the first incomplete step
    for (let i = 1; i <= 6; i++) {
      if (!isStepComplete(i)) {
        setCurrentStep(i as 1 | 2 | 3 | 4 | 5 | 6);
        break;
      }
    }
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, step6Completed]);

  // Ensure correct initial state on mount
  React.useEffect(() => {
    // Set the initial current step and expansion state
    if (completedSteps.length === 0) {
      setCurrentStep(1);
      setStepExpanded(prev => ({
        ...prev,
        1: true
      }));
    }
  }, []); // Run only once on mount

  // Handle pre-loaded resume data from WordDocumentViewer
  useEffect(() => {
    const state = location.state as any;
    if (state?.processedResumeData && state?.step1Complete) {
      setProcessedResumeData(state.processedResumeData);
      setStep1Complete(state.step1Complete);
      setBasicOption(state.basicOption || 'upload');
      toast({
        title: "Resume loaded successfully!",
        description: `Resume from ${state.fileName || 'document viewer'} is ready for tailoring.`
      });
    }
  }, [location.state]);

  // Memoize expensive template options to prevent re-creation on every render
  const templateOptions = useMemo(() => [{
    value: 'template1',
    label: 'Classic Professional',
    preview: Template1
  }, {
    value: 'template2',
    label: 'Modern Minimalist',
    preview: Template2
  }, {
    value: 'template3',
    label: 'Creative Bold',
    preview: Template3
  }, {
    value: 'template4',
    label: 'Executive',
    preview: Template4
  }, {
    value: 'template5',
    label: 'Academic',
    preview: Template5
  }, {
    value: 'template6',
    label: 'Tech Professional',
    preview: Template6
  }, {
    value: 'template7',
    label: 'Healthcare',
    preview: Template7
  }, {
    value: 'template8',
    label: 'Financial Services',
    preview: Template8
  }, {
    value: 'template9',
    label: 'Marketing & Sales',
    preview: Template9
  }, {
    value: 'template10',
    label: 'Startup & Innovation',
    preview: Template10
  }], []);

  // Memoize heavy calculations and options
  const revisionOptions = useMemo(() => [{
    id: 'remove-redundancies',
    emoji: '✂️',
    label: 'Remove Redundancies'
  }, {
    id: 'change-designs',
    emoji: '🎨',
    label: 'Change Designs/Templates'
  }, {
    id: 'improve-formatting',
    emoji: '📝',
    label: 'Improve the Formatting'
  }, {
    id: 'remove-skills-gaps',
    emoji: '🔧',
    label: 'Remove Skills Gaps'
  }, {
    id: 'highlight-transferable',
    emoji: '🌟',
    label: 'Highlight Transferable Skills'
  }, {
    id: 'optimize-work-history',
    emoji: '📈',
    label: 'Optimize Work History'
  }, {
    id: 'add-metrics',
    emoji: '📊',
    label: 'Add Metrics & Numbers'
  }, {
    id: 'fact-check-content',
    emoji: '🔍',
    label: 'Fact Check Content for Accuracy'
  }], []);

  // Memoize writing styles to prevent re-creation
  const writingStyles = useMemo(() => [{
    value: "chronological",
    title: "Chronological Resume",
    description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
    tag: "Default"
  }, {
    value: "functional",
    title: "Functional Resume",
    description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
    tag: "Career Change"
  }, {
    value: "combination",
    title: "Combination (Hybrid) Resume",
    description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
    tag: "Balanced"
  }, {
    value: "targeted",
    title: "Targeted Resume",
    description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
    tag: "Focused"
  }, {
    value: "executive",
    title: "Executive Resume",
    description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
    tag: "Leadership"
  }, {
    value: "creative",
    title: "Creative Resume",
    description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
    tag: "Design"
  }, {
    value: "federal",
    title: "Federal Resume",
    description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
    tag: "Government"
  }, {
    value: "academic",
    title: "Academic / CV",
    description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
    tag: "Academic"
  }], []);
  // Enhanced company extraction with multiple patterns
  const extractCompanyFromJobData = (jobDesc: string) => {
    if (!jobDesc) return '';
    const lines = jobDesc.split('\n');

    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();

      // Pattern 1: "Company: XYZ"
      if (lowerLine.startsWith('company:')) {
        return line.replace(/^company:\s*/i, '').trim();
      }

      // Pattern 2: "Organization: XYZ" 
      if (lowerLine.startsWith('organization:')) {
        return line.replace(/^organization:\s*/i, '').trim();
      }

      // Pattern 3: "Employer: XYZ"
      if (lowerLine.startsWith('employer:')) {
        return line.replace(/^employer:\s*/i, '').trim();
      }

      // Pattern 4: "At XYZ Company" or "at XYZ"
      if (lowerLine.includes(' at ') && line.length < 100) {
        const match = line.match(/\bat\s+([^,\n]+)/i);
        if (match) return match[1].trim();
      }

      // Pattern 5: "XYZ is seeking" or "XYZ seeks"
      if ((lowerLine.includes(' is seeking') || lowerLine.includes(' seeks')) && line.length < 100) {
        const match = line.match(/^([^,\n]+?)\s+(?:is seeking|seeks)/i);
        if (match) return match[1].trim();
      }
    }

    // Pattern 6: Look for company names in first few lines (common job posting format)
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].trim();
      if (line && !line.toLowerCase().includes('job') && !line.toLowerCase().includes('position') && line.length > 2 && line.length < 80 && !line.includes('@')) {
        // Skip if it looks like a job title or email
        const lowerLine = line.toLowerCase();
        if (!lowerLine.includes('engineer') && !lowerLine.includes('manager') && !lowerLine.includes('director') && !lowerLine.includes('specialist') && !lowerLine.includes('analyst') && !lowerLine.includes('developer')) {
          return line;
        }
      }
    }
    return '';
  };

  // Enhanced job title extraction with fallback logic
  const extractJobTitleFromDescription = (jobDesc: string) => {
    if (!jobDesc) return '';
    const lines = jobDesc.split('\n');

    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      const trimmedLine = line.trim();

      // Pattern 1: "Job Title: XYZ" or "Position: XYZ"
      if (lowerLine.startsWith('job title:') || lowerLine.startsWith('position:') || lowerLine.startsWith('role:')) {
        return trimmedLine.replace(/^(job title|position|role):\s*/i, '').trim();
      }

      // Pattern 2: Look for job-related keywords in first few lines
      if (trimmedLine && trimmedLine.length < 100) {
        const jobKeywords = ['engineer', 'manager', 'director', 'specialist', 'analyst', 'developer', 'coordinator', 'supervisor', 'lead', 'senior', 'junior', 'associate', 'assistant'];
        if (jobKeywords.some(keyword => lowerLine.includes(keyword))) {
          return trimmedLine;
        }
      }
    }

    // Pattern 3: First non-empty line if it looks like a job title
    const firstLine = lines.find(line => line.trim())?.trim() || '';
    if (firstLine && firstLine.length < 100 && !firstLine.includes('@') && !firstLine.toLowerCase().includes('company')) {
      return firstLine;
    }
    return '';
  };

  // Enhanced fallback functions
  const getJobTitleFallback = (source: string, data: any) => {
    console.log('getJobTitleFallback called with:', {
      source,
      data
    });
    switch (source) {
      case 'job-description':
        const titleFromData = data?.title || extractJobTitleFromDescription(data?.description || '');
        console.log('Extracted title from job description:', titleFromData);
        return titleFromData || 'Target Position';
      case 'job-position':
        console.log('Using jobPositionTitle:', jobPositionTitle);
        return jobPositionTitle || 'Target Position';
      case 'institution':
        const instTitle = institutionName ? `Position at ${institutionName}` : 'Target Position';
        console.log('Institution title:', instTitle);
        return instTitle;
      case 'assessment-questions':
        return 'Assessment Questions Optimization';
      default:
        return 'Target Position';
    }
  };
  const getCompanyNameFallback = (source: string, data: any) => {
    console.log('getCompanyNameFallback called with:', {
      source,
      data
    });
    switch (source) {
      case 'job-description':
        const companyFromData = extractCompanyFromJobData(data?.description || '');
        console.log('Extracted company from job description:', companyFromData);
        return companyFromData || 'Target Company';
      case 'job-position':
        return 'Target Company';
      case 'institution':
        console.log('Using institutionName as company:', institutionName);
        return institutionName || 'Target Company';
      case 'assessment-questions':
        return 'Target Company';
      default:
        return 'Target Company';
    }
  };

  // Clear all fields function
  const clearAllFields = () => {
    setBasicOption('');
    setSelectedResumeId('');
    setSelectedUploadMethod('');
    setSelectedLinkedInMethod('');
    setLinkedInUrl('');
    setUploadedFile(null);
    setPastedResumeText('');
    setSelectedJobId('');
    setJobDescription('');
    setJobPositionTitle('');
    setJobPositionIndustry('');
    setInstitutionType('');
    setInstitutionName('');
    setMissionAlignment('');
    setAdditionalInstructions('');
    setSelectedDocuments(['resume']);
    setTailoringSource('');
    setGenerateAnalysis(true);
    setStep1Complete(false);
    setProcessedResumeData('');
    setJobDataReady(false);
    setProcessedJobData({
      title: '',
      description: ''
    });
    setMentionCompanyName(false);
    setMentionCompanyMission(false);
    setMentionEducation(false);
    setFeatureSolutionAsset(false);
    setWorkExperienceFramework('none');
    setTransferableSkillsFocus(false);
    setSkillRelevanceOptimization(false);
    setIrrelevantJobFiltering(false);
    setSelectedTemplateMain('');
    setSectionsToTailor(['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline']);
    setCareerTransitionOptions([]);
    setQuantificationLevel('');
    setWritingStyle('chronological');
    setUploadedFiles([]);
    setAdditionalDocuments([]);
    setShowAdvancedOptions(false);
    setShowAdvancedOptionsJob(false);
    setShowAdvancedOptionsInstitution(false);
    setSelectedTailoringType('basic');
    setShowAdvancedWizard(false);
    setJobAnalysis(null);
    setSelectedResponsibilities([]);
    setSelectedRequirements([]);
    setSelectedSkills([]);
    setSelectedSavedAnalysisId('');
    setAssessmentQuestions('');
    setSelectedAssessmentMethod('');
    setSolutionAssetInSummary(false);
  };

  // Check if institution type suggests academic/nonprofit/religious for expanded document options
  const showExpandedDocuments = institutionType && ['academic', 'nonprofit', 'religious'].includes(institutionType);

  // Load user resumes when 'existing' option is selected
  const loadUserResumes = async () => {
    console.log('loadUserResumes called, user:', user?.id);
    if (!user?.id) {
      console.log('No user ID available');
      toast({
        title: "Authentication required",
        description: "Please log in to access your saved resumes.",
        variant: "destructive"
      });
      return;
    }
    setLoadingResumes(true);
    console.log('Loading resumes for user:', user.id);
    try {
      const {
        resumes,
        error
      } = await ResumeService.getUserResumes(user.id);
      console.log('Resume loading result:', {
        resumes: resumes?.length || 0,
        error
      });
      if (error) {
        console.error('Resume loading error:', error);
        toast({
          title: "Error loading resumes",
          description: error.message || "Could not load your saved resumes.",
          variant: "destructive"
        });
      } else {
        console.log('Successfully loaded resumes:', resumes);
        setUserResumes(resumes || []);
        if (!resumes || resumes.length === 0) {
          toast({
            title: "No resumes found",
            description: "Upload your first resume to get started with tailoring."
          });
        }
      }
    } catch (error) {
      console.error('Resume loading exception:', error);
      toast({
        title: "Error loading resumes",
        description: error instanceof Error ? error.message : "Could not load your saved resumes.",
        variant: "destructive"
      });
    } finally {
      setLoadingResumes(false);
    }
  };

  // Load saved jobs when 'existing' job option is selected
  const loadSavedJobs = async () => {
    if (!user?.id) return;
    setLoadingJobs(true);
    try {
      const {
        JobLibraryService
      } = await import('@/services/jobLibraryService');
      const jobs = await JobLibraryService.getSavedJobs();
      setSavedJobs(jobs);
    } catch (error) {
      toast({
        title: "Error loading jobs",
        description: "Could not load your saved job descriptions.",
        variant: "destructive"
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  // Load saved job analyses for advanced tailoring
  const loadSavedJobAnalyses = async () => {
    if (!user?.id) return;
    setLoadingSavedAnalyses(true);
    try {
      const analyses = await JobAnalysisService.getSavedJobAnalyses();
      setSavedJobAnalyses(analyses);
    } catch (error) {
      toast({
        title: "Error loading saved analyses",
        description: "Could not load your saved job analyses.",
        variant: "destructive"
      });
    } finally {
      setLoadingSavedAnalyses(false);
    }
  };

  // Handle saved analysis selection
  const handleSavedAnalysisSelection = (analysisId: string) => {
    if (analysisId === 'clear') {
      // Clear selection
      setSelectedSavedAnalysisId('');
      setJobAnalysis(null);
      setSelectedResponsibilities([]);
      setSelectedRequirements([]);
      setSelectedSkills([]);
      return;
    }
    setSelectedSavedAnalysisId(analysisId);
    const selectedAnalysis = savedJobAnalyses.find(a => a.id === analysisId);
    if (selectedAnalysis) {
      setJobAnalysis({
        jobTitle: selectedAnalysis.job_title,
        industry: selectedAnalysis.industry,
        responsibilities: selectedAnalysis.all_responsibilities,
        requirements: selectedAnalysis.all_requirements,
        skills: selectedAnalysis.all_skills
      });
      setSelectedResponsibilities(selectedAnalysis.selected_responsibilities);
      setSelectedRequirements(selectedAnalysis.selected_requirements);
      setSelectedSkills(selectedAnalysis.selected_skills);
      setJobPositionTitle(selectedAnalysis.job_title);
      if (selectedAnalysis.industry) {
        setJobPositionIndustry(selectedAnalysis.industry);
      }
    }
  };

  // Handle file upload for new resume
  const handleResumeUpload = async (text: string, filename?: string, fileUrl?: string, originalFile?: File) => {
    // Save to resume library if we have the original file
    if (originalFile && user?.id) {
      try {
        const resumeTitle = filename ? filename.replace(/\.[^/.]+$/, '') : 'Untitled Resume';
        await ResumeLibraryService.saveResumeWithFile(resumeTitle, originalFile);
        toast({
          title: "Resume saved to library",
          description: `${resumeTitle} has been added to your resume library.`
        });
      } catch (error) {
        console.error('Failed to save resume to library:', error);
        // Don't show error toast since the main process still works
      }
    }
    // If we have a fileUrl, use ConvertAPI to extract text from PDF
    if (fileUrl && fileUrl.includes('http')) {
      try {
        toast({
          title: "Processing PDF...",
          description: "Extracting text from your PDF document."
        });
        const result = await convertPDFToText(fileUrl);
        if (result.success && result.text) {
          // Use the extracted text from ConvertAPI
          setProcessedResumeData(result.text);
          setStep1Complete(true);
          toast({
            title: "PDF processed successfully!",
            description: "Text extracted from your PDF. Resume data is ready."
          });

          // Save to tailored library with extracted content (as "before" version)
          if (filename && user?.id) {
            TailoredResumeService.saveBeforeResume({
              title: filename.replace(/\.[^/.]+$/, '') || 'Untitled Resume',
              content: result.text,
              file_name: filename,
              file_type: 'text/plain'
            }).catch(error => {
              console.error('Failed to save before resume to tailored library:', error);
            });
          }
          return;
        } else {
          console.error('ConvertAPI error:', result.error);
          toast({
            title: "PDF processing failed",
            description: result.error || "Could not extract text from PDF. Using uploaded file as fallback.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error during PDF conversion:', error);
        toast({
          title: "PDF processing failed",
          description: "Could not extract text from PDF. Using uploaded file as fallback.",
          variant: "destructive"
        });
      }
    }

    // Fallback to original behavior if scraping fails or no URL
    setProcessedResumeData(text);
    setStep1Complete(true);
    toast({
      title: "Resume processed successfully!",
      description: "Your resume data has been loaded. Proceed to Step 2."
    });

    // Save to tailored library (as "before" version) if we have a filename and user
    if (filename && user?.id) {
      TailoredResumeService.saveBeforeResume({
        title: filename.replace(/\.[^/.]+$/, '') || 'Untitled Resume',
        content: text,
        file_name: filename,
        file_type: 'text/plain'
      }).catch(error => {
        console.error('Failed to save before resume to tailored library:', error);
      });
    }
  };

  // Handle additional document upload
  const handleAdditionalDocumentUpload = (text: string, filename: string) => {
    // Create a new File object for storage (since we only get text back)
    const file = new File([text], filename, {
      type: 'text/plain'
    });
    setAdditionalDocuments(prev => [...prev, {
      file,
      extractedText: text
    }]);
  };

  // Handle basic option change
  const handleBasicOptionChange = (value: string) => {
    setBasicOption(value);
    if (value === 'existing') {
      loadUserResumes();
    }
    // Reset sub-selections when changing main option
    setSelectedResumeId('');
    setSelectedUploadMethod('');
    setSelectedLinkedInMethod('');
    setUploadedFile(null);
    setPastedResumeText('');
    setLinkedInUrl('');
    setShowResumeDropdown(true);
  };

  // Handle job upload method change
  const handleJobUploadMethodChange = (value: string) => {
    setSelectedUploadMethod(value);
    if (value === 'existing') {
      loadSavedJobs();
    }
    // Reset selections and job data ready state
    setSelectedJobId('');
    setJobDescription('');
    setJobDataReady(false);
    setShowJobDropdown(true);
    setProcessedJobData({
      title: '',
      description: ''
    });
  };

  // Handle job selection from existing jobs
  const handleJobSelection = (jobId: string) => {
    console.log('Debug - Job selection:', jobId, savedJobs.length);
    setSelectedJobId(jobId);
    setShowJobDropdown(false);
    const selectedJob = savedJobs.find(job => job.id === jobId);
    console.log('Debug - Found selected job:', selectedJob);
    if (selectedJob) {
      // Construct job description from the saved job data
      const jobDesc = `Company: ${selectedJob.companyName}
Job Title: ${selectedJob.jobTitle}
Company Mission: ${selectedJob.companyMission}

Key Responsibilities:
${selectedJob.selectedResponsibilities.map(resp => `• ${resp}`).join('\n')}

Requirements:
${selectedJob.selectedRequirements.map(req => `• ${req}`).join('\n')}

Required Skills:
${selectedJob.selectedSkills.map(skill => `• ${skill}`).join('\n')}`;
      console.log('Debug - Setting job data:', {
        title: selectedJob.jobTitle,
        descLength: jobDesc.length
      });
      setProcessedJobData({
        title: selectedJob.jobTitle,
        description: jobDesc
      });
      setJobDataReady(true);
      markStepComplete(4, true); // Mark step 4 as completed when job is selected

      // Show success toast
      toast({
        title: "Job Selected Successfully!",
        description: `${selectedJob.jobTitle} at ${selectedJob.companyName} is ready for tailoring.`
      });
    } else {
      console.log('Debug - No job found, clearing data');
      setJobDataReady(false);
      setStep4Completed(false); // Reset step 4 completion when job is cleared
      setProcessedJobData({
        title: '',
        description: ''
      });
    }
  };

  // Handle job description paste
  const handleJobDescriptionChange = (value: string) => {
    setJobDescription(value);
    if (value.trim()) {
      setProcessedJobData({
        title: 'Position from Job Description',
        description: value.trim()
      });
      setJobDataReady(true);
      markStepComplete(4, true); // Mark step 4 as completed when job description is provided
    } else {
      setJobDataReady(false);
      markStepComplete(4, false);
      setProcessedJobData({
        title: '',
        description: ''
      });
    }
  };

  // Process Step 1 - Actually load/process the selected resume data
  const processStep1 = async () => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to continue.",
        variant: "destructive"
      });
      return;
    }
    setProcessingStep1(true);
    try {
      let resumeContent = '';
      let resumeSource = '';
      if (basicOption === 'existing' && selectedResumeId) {
        // Load existing resume
        const {
          resume,
          error
        } = await ResumeService.getResume(selectedResumeId);
        if (error || !resume) {
          throw new Error('Failed to load selected resume');
        }
        resumeContent = resume.content;
        resumeSource = `existing resume "${resume.title}"`;
      } else if (basicOption === 'upload' && processedResumeData) {
        // Resume already processed by ResumeUpload component
        resumeContent = processedResumeData;
        resumeSource = 'uploaded file';
      } else if (basicOption === 'paste' && pastedResumeText) {
        // Use pasted resume text
        resumeContent = pastedResumeText;
        resumeSource = 'pasted text';
      } else if (basicOption === 'linkedin' && linkedInUrl) {
        // Import LinkedIn profile
        const {
          data,
          error
        } = await supabase.functions.invoke('import-linkedin-profile', {
          body: {
            url: linkedInUrl
          }
        });
        if (error) throw error;
        resumeContent = data.resume_text;
        resumeSource = 'LinkedIn profile';
      } else {
        throw new Error('Please complete your resume selection');
      }

      // Validate resume content
      if (!resumeContent || resumeContent.trim().length < 100) {
        throw new Error('Resume content appears to be too short or empty. Please check your resume.');
      }

      // Process resume content (using same logic as upload flow)
      setProcessedResumeData(resumeContent);
      setStep1Complete(true);
      // Don't set tailoring source here - let user choose in step 2
      toast({
        title: "Resume processed successfully!",
        description: `Your ${resumeSource} has been loaded. Proceed to Step 2.`
      });
    } catch (error: any) {
      toast({
        title: "Error processing resume",
        description: error.message || "Failed to process your resume. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingStep1(false);
    }
  };

  // State for progress tracking
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Simulate progress bar that takes about 30 seconds
  const simulateAnalysisProgress = () => {
    setAnalysisProgress(0);
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev < 85) {
          // Increment by 2-4% every 1-2 seconds to reach 85% in ~30 seconds
          const increment = Math.random() * 2 + 2;
          const timeFactor = prev < 70 ? 1 : 0.5; // Slow down after 70%
          return Math.min(prev + increment * timeFactor, 85);
        }
        return prev;
      });
    }, 1000 + Math.random() * 1000); // Random interval between 1-2 seconds

    return progressInterval;
  };

  // Generate AI job analysis
  const generateJobAnalysis = async () => {
    if (!jobPositionTitle) return;
    setLoadingAnalysis(true);
    const progressInterval = simulateAnalysisProgress();
    try {
      // Wait for at least 30 seconds or until we reach 85%
      const startTime = Date.now();
      while (analysisProgress < 85 && Date.now() - startTime < 30000) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-job-analysis', {
        body: {
          jobTitle: jobPositionTitle,
          industry: jobPositionIndustry
        }
      });
      if (error) throw error;
      if (data.success) {
        // Complete the progress bar
        clearInterval(progressInterval);
        setAnalysisProgress(100);

        // Brief delay to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 500));
        setJobAnalysis(data.analysis);
        // Pre-select all items by default
        setSelectedResponsibilities(data.analysis.responsibilities);
        setSelectedRequirements(data.analysis.requirements);
        setSelectedSkills(data.analysis.skills);
        toast({
          title: "Job analysis complete!",
          description: `Generated tailored questions for ${jobPositionTitle}${jobPositionIndustry ? ` in ${jobPositionIndustry}` : ''}.`
        });
      } else {
        throw new Error(data.error || 'Failed to generate job analysis');
      }
    } catch (error: any) {
      console.error('Job analysis error:', error);
      clearInterval(progressInterval);
      setAnalysisProgress(0);
      toast({
        title: "Analysis failed",
        description: error.message || "Could not generate job-specific questions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingAnalysis(false);
    }
  };
  const removeFile = (index: number) => {
    setAdditionalDocuments(prev => prev.filter((_, i) => i !== index));
  };
  const handleSectionToggle = (section: string) => {
    setSectionsToTailor(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };
  const handleCareerTransitionToggle = (option: string) => {
    setCareerTransitionOptions(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  };
  const handleDocumentToggle = (document: string) => {
    setSelectedDocuments(prev => prev.includes(document) ? prev.filter(doc => doc !== document) : [...prev, document]);
  };
  const [isProcessing, setIsProcessing] = useState(false);
  const [tailoringResults, setTailoringResults] = useState<any>(null);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Progressive progress bar that reaches 95% at 30 seconds
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);
  const startProgressBar = () => {
    setProgressPercentage(1);
    if (progressInterval) {
      clearInterval(progressInterval);
    }
    const interval = setInterval(() => {
      setProgressPercentage(prev => {
        // Gradually increase from 1% to 95% in exactly 30 seconds
        // 94 increments (95 - 1) over 30 seconds = ~319.1ms per increment
        if (prev < 95) {
          return prev + 1;
        }
        return prev;
      });
    }, 319.1); // 319.1ms * 94 increments = ~30 seconds to reach 95%

    setProgressInterval(interval);
  };
  const stopProgressBar = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
  };
  const cancelTailoring = () => {
    setIsProcessing(false);
    setProgressPercentage(0);
    setIsComplete(false);
    stopProgressBar();
    toast({
      title: "Tailoring Cancelled",
      description: "The tailoring process has been stopped.",
      variant: "default"
    });
  };
  const updateProgress = (progress: number, message: string) => {
    // Only update progress if it's higher than current
    if (progress > progressPercentage) {
      setProgressPercentage(progress);
    }
    console.log(`Progress: ${progress}% - ${message}`);
  };

  // Auto-check solution asset option when conditions are met
  React.useEffect(() => {
    if (tailoringSource === 'job-description' && selectedDocuments.includes('solution-asset-document') && solutionAssetInSummary) {
      setFeatureSolutionAsset(true);
    }
  }, [tailoringSource, selectedDocuments, solutionAssetInSummary]);
  const handleSubmit = async () => {
    console.log('=== TAILORING SUBMIT STARTED ===');
    console.log('step1Complete:', step1Complete);
    console.log('processedResumeData length:', processedResumeData?.length || 0);
    console.log('tailoringSource:', tailoringSource);
    if (!step1Complete || !processedResumeData) {
      console.log('ERROR: Resume not processed - step1Complete:', step1Complete, 'processedResumeData length:', processedResumeData?.length || 0);
      toast({
        title: "Resume not processed",
        description: "Please complete Step 1 first by processing your resume.",
        variant: "destructive"
      });
      return;
    }
    if (!tailoringSource) {
      toast({
        title: "Please select a tailoring source",
        description: "Choose what you want to tailor your resume to.",
        variant: "destructive"
      });
      return;
    }
    let jobDesc = '';
    let jobTitle = '';
    if (tailoringSource === 'job-description') {
      console.log('Debug - tailoringSource:', tailoringSource);
      console.log('Debug - selectedUploadMethod:', selectedUploadMethod);
      console.log('Debug - selectedJobId:', selectedJobId);
      console.log('Debug - jobDataReady:', jobDataReady);
      console.log('Debug - processedJobData:', processedJobData);
      if (jobDataReady && processedJobData.description) {
        // Use the processed job data regardless of method
        jobDesc = processedJobData.description;
        jobTitle = processedJobData.title;
        console.log('Debug - Using processed job data - Title:', jobTitle, 'Desc length:', jobDesc.length);
      } else {
        console.log('Debug - Job data not ready or missing');
      }
      if (!jobDesc) {
        toast({
          title: "Job description required",
          description: "Please enter or select a job description.",
          variant: "destructive"
        });
        return;
      }
    }
    if (tailoringSource === 'job-position') {
      if (!jobPositionTitle) {
        toast({
          title: "Job position title required",
          description: "Please enter the job position title.",
          variant: "destructive"
        });
        return;
      }
      jobTitle = jobPositionTitle;
      jobDesc = `Job Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}`;
    }
    if (tailoringSource === 'institution') {
      if (!institutionName) {
        toast({
          title: "Institution name required",
          description: "Please enter the institution/organization name.",
          variant: "destructive"
        });
        return;
      }
      jobTitle = `Position at ${institutionName}`;
      jobDesc = `Institution: ${institutionName}${institutionType ? `\nType: ${institutionType}` : ''}${missionAlignment ? `\nMission Alignment: ${missionAlignment}` : ''}`;
    }
    if (tailoringSource === 'assessment-questions') {
      if (!assessmentQuestions.trim()) {
        toast({
          title: "Assessment questions required",
          description: "Please enter or upload the assessment questions.",
          variant: "destructive"
        });
        return;
      }
      jobTitle = 'Assessment Questions Optimization';
      jobDesc = `Assessment Questions:\n${assessmentQuestions.trim()}`;
    }
    console.log('=== VALIDATION PASSED, STARTING PROCESSING ===');
    setIsProcessing(true);
    setIsComplete(false);

    // Start the gradual progress bar
    startProgressBar();
    try {
      console.log('=== PROCESSING STATE SET TO TRUE ===');
      toast({
        title: "Starting resume tailoring...",
        description: "Your resume is being tailored using TailorWiz AI."
      });
      console.log('=== TOAST DISPLAYED ===');

      // Check if any advanced options are actually selected
      const hasAdvancedOptions = () => {
        return tailoringSource === 'job-position' || mentionCompanyName || mentionCompanyMission || mentionEducation || featureSolutionAsset || solutionAssetInSummary || workExperienceFramework !== 'none' || transferableSkillsFocus || skillRelevanceOptimization || irrelevantJobFiltering || careerTransitionOptions.length > 0 || writingStyle && writingStyle !== 'chronological' || sectionsToTailor.length > 0 && sectionsToTailor.length < 6 // Only if not all default sections
        ;
      };

      // Generate advanced tailoring instructions ONLY if options are selected
      const generateAdvancedInstructions = () => {
        if (!hasAdvancedOptions()) {
          return '';
        }
        const instructions = [];

        // Special instruction for Job Position/Industry tailoring
        if (tailoringSource === 'job-position') {
          instructions.push("SPECIAL TAILORING MODE - JOB POSITION/INDUSTRY: This is a position/industry-based tailoring (no specific job description provided). We will tailor the resume to the job title and industry provided by the user. The AI will research and generate the top 15 most important responsibilities, top 15 key requirements, and top 20 essential skills for this position type within the specified industry, ranked by importance. This creates a comprehensive, AI-generated job profile that serves as our tailoring benchmark. The resume will be optimized against this researched position profile rather than a specific company's job posting.");
        }

        // Career Summary Enhancement instructions
        if (mentionCompanyName) {
          instructions.push("CAREER SUMMARY: Naturally integrate the target company name ONLY in the final sentence of the professional summary when describing how the candidate can help or contribute. CRITICAL: The applicant has NEVER worked for this target company - make no reference of them working at this company unless it was already mentioned in the original resume.");
        }
        if (mentionCompanyMission) {
          instructions.push("MISSION ALIGNMENT: Reference the company's mission statement ONLY at the end of the career summary to demonstrate cultural fit and value alignment. Place mission references in the final sentence after describing qualifications.");
        }
        if (mentionEducation) {
          instructions.push("EDUCATION EMPHASIS: Highlight relevant education credentials prominently in the career summary section.");
        }
        if (featureSolutionAsset || solutionAssetInSummary) {
          instructions.push("SOLUTION ASSET FOCUS: Feature your solution assets and value propositions prominently in the career summary. Emphasize your unique capabilities, methodologies, and approaches that directly solve business problems and deliver measurable results.");
        }

        // Experience Formatting Framework instructions
        if (workExperienceFramework !== 'none') {
          switch (workExperienceFramework) {
            case 'star':
              instructions.push("EXPERIENCE FORMAT: Override default formatting and restructure ALL experience bullet points using the STAR Method (Situation-Task-Action-Result) for compelling storytelling and measurable outcomes.");
              break;
            case 'par':
              instructions.push("EXPERIENCE FORMAT: Override default formatting and restructure ALL experience bullet points using the PAR Method (Problem-Action-Result) to showcase problem-solving capabilities.");
              break;
            case 'arta':
              instructions.push("EXPERIENCE FORMAT: Override default formatting and restructure ALL experience bullet points using the ARTA Method (Action-Result-Tools-Achievement) emphasizing technical skills and tools.");
              break;
            case 'cosr':
              instructions.push("EXPERIENCE FORMAT: Override default formatting and restructure ALL experience bullet points using the COSR Method (Challenge-Outcome-Skills-Results) highlighting effectiveness under pressure.");
              break;
          }
        }

        // Content Optimization instructions
        if (transferableSkillsFocus) {
          instructions.push("TRANSFERABLE SKILLS: Identify and prominently highlight the top 5 transferable hard skills and 3 key responsibilities that directly align with the target role requirements.");
        }
        if (skillRelevanceOptimization) {
          instructions.push("SKILL FILTERING: Remove or minimize bullet points that don't demonstrate transferable skills or target job requirements to maintain focus and relevance.");
        }
        if (irrelevantJobFiltering) {
          instructions.push("EXPERIENCE FILTERING: Minimize unrelated work positions to maximum 2 concise bullets each while preserving relevant qualifications and maintaining professional continuity.");
        }

        // Career Transition Support instructions
        careerTransitionOptions.forEach(option => {
          switch (option) {
            case 'Career/Industry Transition':
              instructions.push("CAREER TRANSITION: Emphasize transferable skills and bridge experience gaps. Reframe past accomplishments to highlight skills relevant to the new industry/role.");
              break;
            case 'Military to Civilian Transition':
              instructions.push("MILITARY TRANSITION: Translate all military terminology, ranks, and accomplishments into civilian-friendly language. Focus on leadership, project management, and technical skills.");
              break;
            case 'Job Relevancy Statements':
              instructions.push("RELEVANCY STATEMENTS: Add connecting statements or brief explanations showing how each past role or experience directly relates to the target position requirements.");
              break;
          }
        });

        // Writing Style instructions (only if not default chronological)
        if (writingStyle && writingStyle !== 'chronological') {
          switch (writingStyle) {
            case 'functional':
              instructions.push("FUNCTIONAL FORMAT: Group achievements and responsibilities under relevant skill categories rather than by employer. Minimize emphasis on specific dates and employers. Focus on transferable skills and accomplishments that align with target role requirements.");
              break;
            case 'combination':
              instructions.push("COMBINATION FORMAT: Begin with a strong skills summary highlighting top qualifications, followed by detailed chronological work history. Balance emphasis between skills demonstration and career progression. Show both competencies and professional development.");
              break;
            case 'targeted':
              instructions.push("TARGETED FORMAT: Ensure every section directly aligns with the specific job requirements. Prioritize relevant experience and skills. Remove or minimize anything not directly applicable to the target position. Use keywords from the job posting throughout.");
              break;
            case 'executive':
              instructions.push("EXECUTIVE FORMAT: Use boardroom-ready language and emphasize strategic leadership impact. Focus on quantifiable business results, scope of responsibility, and organizational influence. Include P&L responsibility, team size, and budget management. May extend to 2-3 pages with detailed accomplishments.");
              break;
            case 'creative':
              instructions.push("CREATIVE FORMAT: Incorporate visual elements and innovative layouts while maintaining professionalism. Highlight creative projects, portfolio work, and innovative solutions. Use industry-specific terminology and showcase design thinking or creative problem-solving abilities.");
              break;
            case 'federal':
              instructions.push("FEDERAL FORMAT: Include comprehensive detail with exact employment dates, hours worked per week, salary history, and supervisor contact information. Use specific federal keywords and KSAs (Knowledge, Skills, Abilities). Provide detailed duty descriptions and include all relevant experience, education, and training.");
              break;
            case 'academic':
              instructions.push("ACADEMIC/CV FORMAT: Include comprehensive sections for publications, presentations, research, grants, teaching experience, and service. Use academic formatting conventions and include all relevant scholarly activities. Emphasize research contributions, peer-reviewed publications, and academic achievements.");
              break;
          }
        }

        // Sections to tailor (only if custom selection, not all default sections)
        if (sectionsToTailor.length > 0 && sectionsToTailor.length < 6) {
          instructions.push(`SECTIONS TO TAILOR: Focus tailoring efforts specifically on these sections: ${sectionsToTailor.join(', ')}.`);
        }
        return instructions.join('\n\n');
      };

      // For now, don't send any additional instructions or documents to VectorShift
      let combinedInstructions = '';
      console.log('=== VECTORSHIFT CALL DEBUG ===');
      console.log('Resume data length:', processedResumeData?.length || 0);
      console.log('Job description length:', jobDesc?.length || 0);
      console.log('Selected documents:', selectedDocuments);
      console.log('Combined instructions being sent to VectorShift:');
      console.log(combinedInstructions);

      // Prepare debug data with enhanced extraction
      console.log('=== EXTRACTION DEBUG ===');
      console.log('tailoringSource:', tailoringSource);
      console.log('processedJobData:', processedJobData);
      console.log('jobPositionTitle:', jobPositionTitle);
      console.log('institutionName:', institutionName);
      const extractedCompany = getCompanyNameFallback(tailoringSource, processedJobData);
      const extractedJobTitle = getJobTitleFallback(tailoringSource, processedJobData);
      console.log('extractedCompany:', extractedCompany);
      console.log('extractedJobTitle:', extractedJobTitle);
      const requestBody = {
        resumeText: processedResumeData,
        jobDescription: jobDesc,
        additionalNotes: "",
        companyName: extractedCompany,
        jobTitle: extractedJobTitle
      };
      const debugEntry = {
        timestamp: new Date().toISOString(),
        resumeText: processedResumeData,
        jobDescription: jobDesc,
        requestBody: requestBody,
        response: null,
        error: null
      };

      // Use Anthropic for job-position tailoring, VectorShift for others
      let data, error;
      
      if (tailoringSource === 'job-position') {
        console.log('=== ROUTING JOB POSITION TO VECTORSHIFT ===');
        
        // Create synthetic job description from job title + industry for VectorShift
        const syntheticJobDescription = `
Position: ${jobPositionTitle}
Industry: ${jobPositionIndustry || 'General'}

Please tailor this resume for a ${jobPositionTitle} position in the ${jobPositionIndustry || 'general'} industry.

${additionalInstructions ? `Additional Notes: ${additionalInstructions}` : ''}
`.trim();

        const jobPositionRequestBody = {
          resumeText: processedResumeData,
          jobDescription: syntheticJobDescription,
          selectedDocuments: selectedDocuments,
          jobTitle: jobPositionTitle,
          companyName: ''
        };
        
        console.log('Job position request body:', jobPositionRequestBody);
        
        const response = await supabase.functions.invoke('tailor-resume', {
          body: jobPositionRequestBody
        });
        
        data = response.data;
        error = response.error;
      } else {
        console.log('=== CALLING VECTORSHIFT API ===');
        
        // Use custom fetch with 5-minute timeout + retries (WORKER_LIMIT happens when Supabase is temporarily out of capacity)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes

        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) {
          clearTimeout(timeoutId);
          throw new Error('You are not logged in. Please sign in again and retry.');
        }

        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const fetchResponse = await fetch(
              `https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`,
                  'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I'
                },
                body: JSON.stringify(requestBody),
                signal: controller.signal
              }
            );

            if (fetchResponse.status === 546) {
              const txt = await fetchResponse.text();
              if (attempt < maxAttempts) {
                const backoffMs = 1000 * Math.pow(2, attempt - 1);
                console.warn(`WORKER_LIMIT (attempt ${attempt}/${maxAttempts}). Retrying in ${backoffMs}ms...`);
                await new Promise((r) => setTimeout(r, backoffMs));
                continue;
              }
              error = new Error(`Supabase is temporarily overloaded (WORKER_LIMIT). Please retry in a moment. Details: ${txt}`);
              break;
            }

            if (!fetchResponse.ok) {
              const errorText = await fetchResponse.text();
              error = new Error(`HTTP ${fetchResponse.status}: ${errorText}`);
              break;
            }

            data = await fetchResponse.json();
            error = null;
            break;
          } catch (fetchError: any) {
            if (fetchError?.name === 'AbortError') {
              error = new Error('Request timed out after 5 minutes. The AI is taking longer than expected. Please try again.');
              break;
            }

            if (attempt < maxAttempts) {
              const backoffMs = 1000 * Math.pow(2, attempt - 1);
              console.warn(`Tailor request failed (attempt ${attempt}/${maxAttempts}). Retrying in ${backoffMs}ms...`, fetchError);
              await new Promise((r) => setTimeout(r, backoffMs));
              continue;
            }

            error = fetchError;
            break;
          }
        }

        clearTimeout(timeoutId);
      }

      // Update debug entry with response/error
      if (error) {
        debugEntry.error = error.message || JSON.stringify(error);
      } else {
        debugEntry.response = data;
      }

      // Add to debug data and persist
      const newDebugData = [debugEntry, ...debugData.slice(0, 9)]; // Keep last 10 entries
      setDebugData(newDebugData);
      localStorage.setItem('tailoring-debug-data', JSON.stringify(newDebugData));
      console.log('=== VECTORSHIFT API CALL COMPLETED ===');
      console.log('=== VECTORSHIFT RESPONSE DEBUG ===');
      console.log('Error:', error);
      console.log('Data success:', data?.success);
      console.log('Raw data keys:', Object.keys(data || {}));
      console.log('Data structure:', JSON.stringify(data, null, 2));
      if (error) {
        console.error('Tailor resume API error:', error);
        throw error;
      }
      if (!data || !data.success) {
        console.error('Tailor resume processing failed:', data?.error || 'No data returned');
        throw new Error(data?.error || 'Tailor resume processing failed');
      }

      // Additional validation
      console.log('=== RESPONSE VALIDATION ===');
      console.log('Has Final_Resume_Text:', !!data.Final_Resume_Text);
      console.log('Has Cover_Letter:', !!data.Cover_Letter);
      console.log('Has changes_analysis:', !!data.changes_analysis);
      console.log('Changes analysis type:', typeof data.changes_analysis);
      if (data.changes_analysis && typeof data.changes_analysis === 'object') {
        console.log('Changes analysis keys:', Object.keys(data.changes_analysis));
        console.log('Keywords count:', data.changes_analysis.keywords_added?.length || 0);
        console.log('Skills count:', data.changes_analysis.skills_emphasized?.length || 0);
      }
      if (error) {
        console.error('Tailor resume API error:', error);
        throw error;
      }
      if (!data || !data.success) {
        console.error('Tailor resume processing failed:', data?.error || 'No data returned');
        throw new Error(data?.error || 'Tailor resume processing failed');
      }

      // Transform the response to match expected format
      const vectorShiftResults = {
        Final_Resume_Text: data.Final_Resume_Text || data.data?.tailoredText,
        Cover_Letter: data.Cover_Letter,
        Thank_You_Letter: data.Thank_You_Letter,
        Executive_Bio: data.Executive_Bio,
        Linkedin_Profile: data.Linkedin_Profile,
        Outreach_Letter: data.Outreach_Letter,
        analysis_results: data.analysis_results || data.analysisData,
        original_analysis: data.original_analysis || data.analysisData?.original_analysis,
        tailored_analysis: data.tailored_analysis || data.analysisData?.tailored_analysis,
        changes_analysis: data.changes_analysis || data.analysisData?.changes_analysis,
        company_name: data.company_name || data.jobInfo?.company,
        job_title: data.job_title || data.jobInfo?.title,
        match_score: data.match_score ?? data.data?.matchScore ?? null,
        original_match_score: data.original_match_score ?? data.data?.originalMatchScore ?? null,
        improvements: data.improvements || data.data?.improvements,
        changes: data.changes || data.data?.changes,
        // Anthropic extras (present only for job-position flow)
        anthropic_metadata: (data as any).anthropic_metadata,
        anthropic_prompt: (data as any).anthropic_prompt,
        anthropic_raw_text: (data as any).anthropic_raw_text
      };
      console.log('=== VECTORSHIFT RESULTS EXTRACTED ===');
      console.log('VectorShift results keys:', Object.keys(vectorShiftResults || {}));
      console.log('Raw edge function response keys:', Object.keys(data || {}));
      // Safe preview helper for logging (handles objects and strings)
      const getContentPreview = (content: any): string => {
        if (!content) return 'NO CONTENT';
        if (typeof content === 'string') return content.substring(0, 200);
        if (typeof content === 'object') return JSON.stringify(content).substring(0, 200);
        return String(content).substring(0, 200);
      };
      const getContentLength = (content: any): number => {
        if (!content) return 0;
        if (typeof content === 'string') return content.length;
        if (typeof content === 'object') return JSON.stringify(content).length;
        return 0;
      };
      console.log('Final_Resume_Text length:', getContentLength(vectorShiftResults.Final_Resume_Text));
      console.log('Cover_Letter length:', getContentLength(vectorShiftResults.Cover_Letter));
      console.log('Cover_Letter content preview:', getContentPreview(vectorShiftResults.Cover_Letter));
      console.log('Thank_You_Letter length:', getContentLength(vectorShiftResults.Thank_You_Letter));
      console.log('Executive_Bio length:', getContentLength(vectorShiftResults.Executive_Bio));
      console.log('Analysis data structure:', data.analysis_results || data.analysisData);
      console.log('Original analysis extracted:', vectorShiftResults.original_analysis);
      console.log('Tailored analysis extracted:', vectorShiftResults.tailored_analysis);
      console.log('Match scores - Original:', vectorShiftResults.original_match_score, 'Tailored:', vectorShiftResults.match_score);

      // Stop the gradual progress and set to 100%
      stopProgressBar();
      setProgressPercentage(100);
      setIsComplete(true);
      setTailoringResults(vectorShiftResults);

      // Navigate to results page after a brief delay to show 100% completion
      setTimeout(() => {
        console.log('=== NAVIGATION DEBUG ===');
        console.log('Navigating to /resume-results with raw Anthropic data from test page');
        // Navigate to resume-results with raw Anthropic data from the TEST page
        navigate('/resume-results', {
          state: {
            rawAnthropicResponse: (debugEntry as any).anthropicRaw,
            anthropicRequest: (debugEntry as any).anthropicRequest,
            // Also pass formatted results/state
            tailoringResults: vectorShiftResults,
            originalResume: processedResumeData,
            jobTitle: vectorShiftResults?.job_title || jobTitle || 'Target Position',
            jobDescription: jobDesc,
            companyName: vectorShiftResults?.company_name || 'Target Company',
            generateAnalysis: generateAnalysis,
            selectedDocuments: selectedDocuments
          }
        });
      }, 500); // Brief delay to show completion

      toast({
        title: "Resume tailoring complete!",
        description: "Your tailored resume is ready. Redirecting to results..."
      });
    } catch (error: any) {
      console.error('Tailoring error:', error);
      setProgressPercentage(0);
      setIsComplete(false);
      toast({
        title: "Tailoring failed",
        description: error.message || "Failed to tailor your resume. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      stopProgressBar();
      // Reset progress after 2 seconds when complete
      if (progressPercentage === 100) {
        setTimeout(() => {
          setProgressPercentage(0);
          setIsComplete(false);
        }, 2000);
      }
    }
  };
  return <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/?view=libraries')} className="h-9 w-9 p-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">Resume Tailoring</h1>
                <p className="text-sm text-muted-foreground">AI-powered resume customization for specific jobs</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
              <span className="text-xs font-medium text-muted-foreground">powered by</span>
              <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-8 px-6 max-w-3xl">
        <div className="w-full">
          {/* Main Content Area */}
          <div className="flex-1 max-w-full">
            {/* Header Section */}
            <div className="mb-12 text-center">
              <div className="space-y-2 mb-8">
                
                
                <h1 className="text-6xl font-extrabold leading-tight tracking-tight relative">
                  <div className="absolute inset-0 bg-background/60 backdrop-blur-md rounded-lg shadow-lg -z-10"></div>
                  <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                    Resume Tailoring{" "}
                  </span>
                  <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                    Studio
                  </span>
                </h1>
                
                  <div className="max-w-3xl mx-auto px-4 mt-4 relative">
                   <div className="absolute inset-0 bg-background/50 backdrop-blur-md rounded-lg shadow-md -z-10"></div>
                   <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                     Tailor Your Resume Package to ANY Opportunity in Under 60 Seconds!
                   </p>
                    <div className="mt-4 relative">
                      {/* Step Icons */}
                      <div className="flex justify-between items-center mb-3 px-1">
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(1) ? 'bg-green-500 text-white' : 
                            currentStep === 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Upload className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(2) ? 'bg-green-500 text-white' : 
                            currentStep === 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Target className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(3) ? 'bg-green-500 text-white' : 
                            currentStep === 3 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Settings className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(4) ? 'bg-green-500 text-white' : 
                            currentStep === 4 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <FileText className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(5) ? 'bg-green-500 text-white' : 
                            currentStep === 5 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <MessageSquare className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(6) ? 'bg-green-500 text-white' : 
                            currentStep === 6 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <CheckCircle className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                        <span className="font-medium">Progress</span>
                        <span className="font-medium">{getStepProgress()}% Complete</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full shadow-lg overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500 ease-out" 
                          style={{ width: `${getStepProgress()}%` }}
                        ></div>
                      </div>
                    </div>
                 </div>
                
                
              </div>
            </div>

            {/* Steps Section - Clean Card Layout */}
            <div className="space-y-4">
              {/* Step cards with clean styling */}
        <Collapsible open={stepExpanded[1]} onOpenChange={() => toggleStepExpansion(1)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(1) ? (stepExpanded[1] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[1] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(1) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(1) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 1: Resume Source</span>
                        {completedSteps.includes(1) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 1 && !completedSteps.includes(1) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                       {completedSteps.includes(1) && !stepExpanded[1] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                           {basicOption === 'existing' && selectedResumeId && userResumes.find(r => r.id === selectedResumeId) ? `Resume: ${userResumes.find(r => r.id === selectedResumeId)?.title || 'Selected Resume'}` : basicOption === 'upload' && uploadedFile ? `Uploaded: ${uploadedFile.name}` : basicOption === 'paste' ? 'Pasted resume text' : 'Resume processed'}
                         </div> : <CardDescription className="text-base">
                          {completedSteps.includes(1) ? "✅ Resume source selected and processed" : "Choose how you want to provide your resume"}
                        </CardDescription>}
                    </div>
                  </div>
                   <div className="flex items-center gap-2">
                     <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[1] ? 'rotate-180' : ''}`} />
                   </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(1) || stepExpanded[1]) && <CollapsibleContent className="animate-accordion-down">
                <CardContent className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                <RadioGroup value={basicOption} onValueChange={handleBasicOptionChange} className="space-y-5">
                <div className={`flex items-center space-x-4 p-5 rounded-2xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${basicOption === 'existing' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                 <RadioGroupItem value="existing" id="existing" className="mt-0.5" />
                 <Label htmlFor="existing" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'existing' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Choose Existing Resume</div>
                    <div className="text-sm text-muted-foreground">Select from saved resumes in your library</div>
                  </div>
                </Label>
              </div>

              {/* Existing Resume Dropdown */}
              {basicOption === 'existing' && <div className="ml-12 space-y-3">
                  <Label className="text-sm font-medium">Select Resume</Label>
                  {loadingResumes ? <div className="p-6 border rounded-lg bg-white">
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm font-medium">Loading your resumes...</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Fetching saved resumes from your library</p>
                        </div>
                    </div> : showResumeDropdown && !selectedResumeId ? <Select value={selectedResumeId} onValueChange={async (value: string) => {
                            if (value === 'upload-new') {
                              setBasicOption('upload');
                              return;
                            }
                            setSelectedResumeId(value);

                            // Automatically process the selected resume
                            if (value && user?.id) {
                              console.log('Processing selected resume:', value);
                              setProcessingStep1(true);
                              try {
                                const {
                                  resume,
                                  error
                                } = await ResumeService.getResume(value);
                                console.log('Resume fetch result:', {
                                  resume: !!resume,
                                  error
                                });
                                if (error || !resume) {
                                  console.error('Resume fetch error:', error);
                                  throw new Error(error?.message || 'Failed to load selected resume');
                                }
                                let resumeContent = '';

                                // Handle different resume types and extract text content
                                // Use stored content if available
                                if (resume.content && resume.content.trim().length > 50) {
                                  console.log('Using stored resume content directly');
                                  resumeContent = resume.content;
                                } else {
                                  console.error('Resume has insufficient stored content, length:', resume.content?.length || 0);
                                  throw new Error('This resume was saved without text content. Please re-upload it to the resume library or use the "Upload New Resume" option.');
                                }
                                if (!resumeContent.trim()) {
                                  console.error('No text content extracted');
                                  throw new Error('No text content could be extracted from the resume');
                                }
                                console.log('Resume processed successfully, content length:', resumeContent.length);
                                setProcessedResumeData(resumeContent);
                                setStep1Complete(true);
                                setShowResumeDropdown(false);
                                toast({
                                  title: "Resume loaded successfully!",
                                  description: "Your resume is ready for tailoring."
                                });
                              } catch (error: any) {
                                console.error('Resume processing error:', error);
                                toast({
                                  title: "Error loading resume",
                                  description: error.message || "Failed to load the selected resume. Please try again.",
                                  variant: "destructive"
                                });
                              } finally {
                                setProcessingStep1(false);
                              }
                            } else {
                              console.log('No value or user ID for resume processing');
                            }
                          }}>
                        <SelectTrigger className="w-full" disabled={processingStep1}>
                          <SelectValue placeholder={processingStep1 ? "Processing selected resume..." : "Choose from your saved resumes"} />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          {userResumes.length > 0 ? userResumes.map(resume => <SelectItem key={resume.id} value={resume.id}>
                                {resume.title || 'Untitled Resume'} - {new Date(resume.updated_at).toLocaleDateString()}
                              </SelectItem>) : <SelectItem value="no-resumes" disabled>
                              No saved resumes found
                            </SelectItem>}
                          <SelectItem value="upload-new">
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              Upload New Resume Instead
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select> : null}
                    
                     {/* Processing Progress */}
                     {processingStep1 && <div className="mt-4 p-4 bg-muted/30 border border-muted rounded-lg">
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 border-2 border-foreground/60 border-t-transparent rounded-full animate-spin" />
                              <span className="text-sm font-medium">Processing Resume...</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Extracting text content and preparing for tailoring</p>
                          </div>
                       </div>}
                     
                     {/* Selected Resume Display */}
                      {selectedResumeId && step1Complete && userResumes.length > 0 && <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-950/20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-950/30 transition-colors" onClick={() => setShowResumeDropdown(true)}>
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-slate-600" />
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Selected Resume:</span>
                          </div>
                          <div className="ml-7 space-y-1">
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                              {userResumes.find(r => r.id === selectedResumeId)?.title || 'Selected Resume'}
                            </div>
                            <div className="text-xs text-slate-700 dark:text-slate-300">
                              Updated: {userResumes.find(r => r.id === selectedResumeId) ? new Date(userResumes.find(r => r.id === selectedResumeId)!.updated_at).toLocaleDateString() : ''}
                            </div>
                          </div>
                        </div>}
                 </div>}
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${basicOption === 'upload' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                 <RadioGroupItem value="upload" id="upload" className="mt-0.5" />
                 <Label htmlFor="upload" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'upload' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                    <Upload className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Upload New Resume</div>
                    <div className="text-sm text-muted-foreground">Accepts PDF, DOC, DOCX, TXT (max 10MB)</div>
                  </div>
                </Label>
              </div>

              {/* Upload Resume Options */}
              {basicOption === 'upload' && <div className="ml-12 mt-4">
                  <DocumentUpload onUploadComplete={handleResumeUpload} title="Upload Resume" description="Upload a PDF, Word document, or paste your resume text" acceptedTypes={['.pdf', '.doc', '.docx']} />
                </div>}
               
                 <div className={`flex items-center space-x-4 p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${basicOption === 'paste' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                  <RadioGroupItem value="paste" id="paste" className="mt-0.5" />
                  <Label htmlFor="paste" className="flex items-center gap-3 cursor-pointer flex-1">
                     <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'paste' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                     <FileText className="w-4 h-4" />
                   </div>
                   <div>
                     <div className="font-medium">Paste Resume Text</div>
                     <div className="text-sm text-muted-foreground">Copy and paste your resume content</div>
                   </div>
                 </Label>
               </div>

                {/* Paste Resume Options */}
                {basicOption === 'paste' && <div className="ml-12 mt-4 space-y-4">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Paste Resume Content</Label>
                      <Textarea placeholder="Paste your resume text here..." className="min-h-[200px]" value={pastedResumeText} onChange={async e => {
                              const content = e.target.value;
                              setPastedResumeText(content);
                              if (content.trim().length > 100) {
                                // Automatically process the pasted content
                                const blob = new Blob([content], {
                                  type: 'text/plain'
                                });
                                const file = new File([blob], 'pasted-resume.txt', {
                                  type: 'text/plain'
                                });
                                setUploadedFile(file);

                                // Auto-process in background
                                setTimeout(() => {
                                  handleResumeUpload(content, 'pasted-resume.txt', undefined, file);
                                }, 500);
                              } else if (content.trim().length === 0) {
                                // Clear processed data if content is empty
                                setUploadedFile(null);
                                setStep1Complete(false);
                                setProcessedResumeData('');
                              }
                            }} />
                      
                      {pastedResumeText && pastedResumeText.length > 100 && !step1Complete && <div className="text-sm text-muted-foreground">
                          {pastedResumeText.length} characters • Processing automatically...
                        </div>}
                    </div>
                  </div>}
              
                <div className="flex items-center space-x-3 p-4 rounded-lg border border-blue-200 bg-blue-50/50 transition-all duration-200">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                    <Linkedin className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-blue-900">LinkedIn Data (Optional)</div>
                    <div className="text-xs text-blue-700">Upload for enhanced tailoring</div>
                  </div>
                  <div className="relative shrink-0">
                    <input type="file" accept=".zip,.json" onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setLinkedInDataFile(file);
                                toast({
                                  title: "LinkedIn Data Uploaded",
                                  description: `File "${file.name}" ready for processing`
                                });
                              }
                            }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" id="linkedin-upload" />
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7" asChild>
                      <label htmlFor="linkedin-upload" className="cursor-pointer flex items-center gap-1">
                        <Upload className="w-3 h-3" />
                        {linkedInDataFile ? 'Change' : 'Upload'}
                      </label>
                    </Button>
                  </div>
                </div>

                {/* Show uploaded LinkedIn file info */}
                 {linkedInDataFile && <div className="ml-4 mt-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                     <div className="flex items-center gap-3">
                       <CheckCircle className="w-5 h-5 text-slate-600" />
                       <div>
                         <div className="font-medium text-slate-800">LinkedIn Data Ready</div>
                         <div className="text-sm text-slate-600">
                           File: {linkedInDataFile.name} ({(linkedInDataFile.size / 1024 / 1024).toFixed(2)} MB)
                         </div>
                       </div>
                      <Button size="sm" variant="ghost" onClick={() => {
                              setLinkedInDataFile(null);
                              toast({
                                title: "File Removed",
                                description: "LinkedIn data file has been removed"
                              });
                            }} className="ml-auto text-slate-600 hover:text-slate-800">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>}

              {/* LinkedIn Import Options */}
              {basicOption === 'linkedin' && <div className="ml-12 mt-4 space-y-4">
                  <Label className="text-sm font-medium">LinkedIn Import Options</Label>
                  <Select value={selectedLinkedInMethod} onValueChange={setSelectedLinkedInMethod}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose import method" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="linkedin-url">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Enter LinkedIn Profile URL
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {selectedLinkedInMethod === 'linkedin-url' && <div className="space-y-3">
                      <Label className="text-sm font-medium">LinkedIn Profile URL</Label>
                      <Input placeholder="https://www.linkedin.com/in/yourprofile" value={linkedInUrl} onChange={e => setLinkedInUrl(e.target.value)} />
                      {linkedInUrl && <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                          <Linkedin className="w-4 h-4 text-primary" />
                          <span className="text-sm flex-1">{linkedInUrl}</span>
                          <CheckCircle className="w-4 h-4 text-slate-500" />
                        </div>}
                    </div>}
                </div>}
                </RadioGroup>
              </CardContent>
              
              {/* Continue Button for Step 1 */}
              {currentStep === 1 && isStepComplete(1) && !completedSteps.includes(1) && <CardContent className="pt-0 rounded-b-lg">
                  <div className="flex justify-end">
                     <Button onClick={continueToNextStep} className="bg-green-600 hover:bg-green-700 text-white">
                      Continue to Step 2
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>}
            </CollapsibleContent>}
          </Card>
        </Collapsible>


        {/* Step 2: Tailoring Target */}
        <Collapsible open={stepExpanded[2]} onOpenChange={() => toggleStepExpansion(2)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(2) ? (stepExpanded[2] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[2] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(2) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(2) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Target className="w-5 h-5" />}
                     </div>
                     <div className="flex-1">
                       <CardTitle className="text-2xl flex items-center gap-3">
                         <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 2: Tailoring Target</span>
                         {completedSteps.includes(2) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                             ✅ COMPLETED
                           </Badge>}
                         {currentStep === 2 && !completedSteps.includes(2) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                             ⚡ ACTIVE
                           </Badge>}
                       </CardTitle>
                       
                       {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(2) && !stepExpanded[2] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {tailoringSource === 'job-description' ? 'Job Description' : tailoringSource === 'job-position' ? `Job Position: ${jobPositionTitle || 'Selected'}` : tailoringSource === 'institution' ? `Institution: ${institutionName || 'Selected'}` : tailoringSource === 'revisions' ? 'Document Revisions' : tailoringSource === 'assessment-questions' ? 'Assessment Questions' : 'Target selected'}
                          </div> : <CardDescription className="text-base">
                           {completedSteps.includes(2) ? "✅ Tailoring target selected" : "Choose what you want to optimize your resume for"}
                         </CardDescription>}
                     </div>
                   </div>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[2] ? 'rotate-180' : ''}`} />
                    </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="animate-accordion-down">
              <CardContent className="space-y-3 rounded-b-lg">
            
            {/* Step 2 Debug - Hidden from users but kept for debugging */}
            {step1Complete && processedResumeData && <div className="hidden mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Step 2 Debug - Text Content</h4>
                
                <div className="space-y-3">
                  <div className="bg-white border rounded p-3">
                    <p className="text-xs font-medium text-blue-600 mb-2">Resume Text ({processedResumeData.length} characters):</p>
                    <div className="max-h-32 overflow-y-auto">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {processedResumeData.substring(0, 300)}
                        {processedResumeData.length > 300 && '...'}
                      </pre>
                    </div>
                  </div>
                  
                  {(jobDescription || tailoringSource === 'job-position' && jobPositionTitle || tailoringSource === 'institution' && institutionName || tailoringSource === 'assessment-questions' && assessmentQuestions) && <div className="bg-white border rounded p-3">
                      <p className="text-xs font-medium text-blue-600 mb-2">
                        {tailoringSource === 'job-description' && 'Job Description Text:'}
                        {tailoringSource === 'job-position' && 'Job Position:'}
                        {tailoringSource === 'institution' && 'Institution:'}
                        {tailoringSource === 'assessment-questions' && 'Assessment Questions:'}
                      </p>
                      <div className="max-h-32 overflow-y-auto">
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                          {tailoringSource === 'job-description' && jobDescription}
                          {tailoringSource === 'job-position' && `Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}`}
                          {tailoringSource === 'institution' && `Institution: ${institutionName}${institutionType ? `\nType: ${institutionType}` : ''}${missionAlignment ? `\nMission: ${missionAlignment}` : ''}`}
                          {tailoringSource === 'assessment-questions' && assessmentQuestions}
                        </pre>
                      </div>
                    </div>}
                </div>
              </div>}
            
            <RadioGroup value={tailoringSource} onValueChange={setTailoringSource}>
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'job-description' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="job-description" id="job-description" className="mt-0.5" />
                 <Label htmlFor="job-description" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'job-description' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                     <FileText className="w-4 h-4" />
                   </div>
                   <div>
                     <div className="font-medium">Job Description</div>
                     <div className="text-sm text-muted-foreground">Tailor to a specific job posting</div>
                   </div>
                 </Label>
               </div>
               
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'job-position' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="job-position" id="job-position" className="mt-0.5" />
                 <Label htmlFor="job-position" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'job-position' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Job Position/Industry</div>
                    <div className="text-sm text-muted-foreground">Tailor to a role type or industry</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'institution' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="institution" id="institution" className="mt-0.5" />
                 <Label htmlFor="institution" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'institution' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Institution/Organization</div>
                    <div className="text-sm text-muted-foreground">Tailor to a specific company or organization</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'revisions' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="revisions" id="revisions" className="mt-0.5" />
                 <Label htmlFor="revisions" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'revisions' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Settings className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Document Revisions & Updates</div>
                    <div className="text-sm text-muted-foreground">Make revisions and updates to existing documents</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'assessment-questions' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="assessment-questions" id="assessment-questions" className="mt-0.5" />
                 <Label htmlFor="assessment-questions" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'assessment-questions' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <BarChart3 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Tailor Answers to Job Assessment Questions</div>
                    <div className="text-sm text-muted-foreground">Optimize for common pre-screening and application questions</div>
                  </div>
                </Label>
              </div>
             </RadioGroup>
              </CardContent>
              
              {/* Continue Button for Step 2 */}
              {currentStep === 2 && isStepComplete(2) && !completedSteps.includes(2) && <CardContent className="pt-0 rounded-b-lg">
                  <div className="flex justify-end">
                    <Button onClick={continueToNextStep} className="bg-green-600 hover:bg-green-700 text-white">
                      Continue to Step 3
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>}
            </CollapsibleContent>
            </Card>
          </Collapsible>



        {/* Step 3: Select Documents to Tailor - For job description */}
        {tailoringSource === 'job-description' && <Collapsible open={stepExpanded[3]} onOpenChange={() => toggleStepExpansion(3)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(3) ? (stepExpanded[3] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[3] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(3) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(3) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 3: Select Documents to Tailor</span>
                          {completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 3 && !completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                         {completedSteps.includes(3) && !stepExpanded[3] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                             Documents selected: {selectedDocuments.map(doc => doc === 'resume' ? 'Resume' : doc === 'cover-letter' ? 'Cover Letter' : doc === 'linkedin-profile' ? 'LinkedIn' : doc.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(', ')}
                           </div> : <CardDescription className="text-base">
                            {completedSteps.includes(3) ? "✅ Documents selected" : "Choose which documents to tailor to this job"}
                          </CardDescription>}
                      </div>
                    </div>
                     <div className="flex items-center gap-2">
                       <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[3] ? 'rotate-180' : ''}`} />
                     </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              {/* Only show content when step is not completed OR when completed but expanded for editing */}
              {(!completedSteps.includes(3) || stepExpanded[3]) && <CollapsibleContent className="animate-accordion-down">
                  <CardContent className="p-6 rounded-b-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter'
                        }, {
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-interview follow-up'
                        }, {
                          id: 'outreach-email',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Networking introduction'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Social media presence'
                        }, {
                          id: 'solution-asset-document',
                          label: 'Solution Asset Document',
                          icon: FileText,
                          description: 'Strategic solution presentation'
                        }, {
                          id: 'hiring-manager-contact-report',
                          label: 'Hiring Manager Contact Report',
                          icon: FileText,
                          description: 'Decision-maker contact strategy'
                        }, {
                          id: 'career-clarity-report',
                          label: 'Career Clarity Report',
                          icon: FileText,
                          description: 'Career direction analysis'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          return <div key={document.id}>
                       <div className={`group relative p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                         {['solution-asset-document', 'hiring-manager-contact-report', 'career-clarity-report'].includes(document.id) && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-2 py-1 font-bold shadow-md">
                             NEW
                           </Badge>}
                         <div className="flex items-start space-x-3">
                              <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                           <div className="flex-1 space-y-2">
                             <div className="flex items-center gap-2">
                               <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                               <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                {document.label}
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {document.description}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Solution Asset Career Summary Option - appears directly under solution asset document */}
                      {document.id === 'solution-asset-document' && selectedDocuments.includes('solution-asset-document') && <div className={`mt-2 p-3 rounded-lg border-2 transition-all duration-300 relative ${solutionAssetInSummary ? 'bg-green-50 border-green-500 shadow-md' : 'bg-blue-50 border-blue-300'}`}>
                          <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                            RECOMMENDED
                          </Badge>
                          <div className="flex items-center gap-3">
                            <Checkbox id="solution-asset-summary" checked={solutionAssetInSummary} onCheckedChange={checked => setSolutionAssetInSummary(checked === true)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor="solution-asset-summary" className={`text-sm font-medium cursor-pointer ${solutionAssetInSummary ? 'text-green-900' : 'text-blue-900'}`}>
                              Feature Solution Asset in the Career Summary?
                            </Label>
                          </div>
                           <p className={`text-xs mt-2 ml-6 ${solutionAssetInSummary ? 'text-slate-700' : 'text-blue-700'}`}>
                            Highlight your solution assets and value propositions in the career summary section.
                          </p>
                        </div>}
                    </div>;
                        })}
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                     <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for tailoring
                    </span>
                  </div>
                </div>}
            </CardContent>
            
            {/* Finish Selection Button for Step 3 - Always show when step 3 is expanded and not completed */}
            {stepExpanded[3] && !step3Completed && <CardContent className="pt-0 rounded-b-lg">
                <div className="flex justify-center">
                  <Button onClick={() => {
                          markStepComplete(3, true);
                          toast({
                            title: "Document selection completed!",
                            description: `${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''} selected for tailoring.`
                          });
                        }} className="bg-green-600 hover:bg-green-700 text-white" disabled={selectedDocuments.length === 0}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finished Selecting Documents
                  </Button>
                </div>
              </CardContent>}
          </CollapsibleContent>}
        </Card>
      </Collapsible>}

        {/* Step 3: Select Documents to Tailor - For job position */}
        {tailoringSource === 'job-position' && <Collapsible open={stepExpanded[3]} onOpenChange={() => toggleStepExpansion(3)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(3) ? (stepExpanded[3] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[3] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(3) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(3) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 3: Select Documents to Tailor</span>
                          {completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 3 && !completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                         {completedSteps.includes(3) && !stepExpanded[3] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                             Documents selected: {selectedDocuments.map(doc => doc === 'resume' ? 'Resume' : doc === 'cover-letter' ? 'Cover Letter' : doc === 'linkedin-profile' ? 'LinkedIn' : doc.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(', ')}
                           </div> : <CardDescription className="text-base">
                            {completedSteps.includes(3) ? "✅ Documents selected" : "Choose which documents to tailor for this position"}
                          </CardDescription>}
                      </div>
                    </div>
                     <div className="flex items-center gap-2">
                       <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[3] ? 'rotate-180' : ''}`} />
                     </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              {/* Only show content when step is not completed OR when completed but expanded for editing */}
              {(!completedSteps.includes(3) || stepExpanded[3]) && <CollapsibleContent className="animate-accordion-down">
                  <CardContent className="p-6 rounded-b-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter'
                        }, {
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-interview follow-up'
                        }, {
                          id: 'outreach-email',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Networking introduction'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Social media presence'
                        }, {
                          id: 'career-clarity-report',
                          label: 'Career Clarity Report',
                          icon: FileText,
                          description: 'Career direction analysis'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          return <div key={document.id}>
                       <div className={`group relative p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                         {['solution-asset-document', 'hiring-manager-contact-report', 'career-clarity-report'].includes(document.id) && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-2 py-1 font-bold shadow-md">
                             NEW
                           </Badge>}
                         <div className="flex items-start space-x-3">
                              <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                           <div className="flex-1 space-y-2">
                             <div className="flex items-center gap-2">
                               <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                               <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                {document.label}
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {document.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
                        })}
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                     <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for tailoring
                    </span>
                  </div>
                </div>}
                
                {/* Finish Selection Button */}
                {!step3Completed && selectedDocuments.length > 0 && <div className="mt-6">
                  <div className="flex justify-center">
                    <Button onClick={() => {
                            markStepComplete(3, true);
                            toast({
                              title: "Document selection completed!",
                              description: `${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''} selected for tailoring.`
                            });
                          }} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finished Selecting Documents
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </CollapsibleContent>}
        </Card>
      </Collapsible>}

        {/* Step 4: Job Description Input - Only show for job description option */}
        {tailoringSource === 'job-description' && <Collapsible open={stepExpanded[4]} onOpenChange={() => toggleStepExpansion(4)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(4) ? (stepExpanded[4] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[4] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(4) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(4) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 4: Job Description</span>
                          {completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 4 && !completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                          {completedSteps.includes(4) && !stepExpanded[4] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                               <CheckCircle className="w-4 h-4 text-green-500" />
                              Job details ready for tailoring
                            </div>}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[4] ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-6 rounded-b-lg">
                  <RadioGroup value={selectedUploadMethod} onValueChange={handleJobUploadMethodChange}>
                   <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'upload' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                    <RadioGroupItem value="upload" id="upload-job" className="mt-0.5" />
                    <Label htmlFor="upload-job" className="flex items-center gap-3 cursor-pointer flex-1">
                       <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'upload' ? 'bg-green-100 text-green-600' : 'bg-white'}`}>
                       <Upload className="w-4 h-4" />
                     </div>
                     <div>
                       <div className="font-medium">Upload New Job</div>
                       <div className="text-sm text-muted-foreground">Upload a job description file or add via Job Wizard</div>
                     </div>
                   </Label>
                 </div>

                 <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'existing' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                   <RadioGroupItem value="existing" id="existing-job" className="mt-0.5" />
                   <Label htmlFor="existing-job" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'existing' ? 'bg-green-100 text-green-600' : 'bg-white'}`}>
                       <FileText className="w-4 h-4" />
                     </div>
                     <div>
                       <div className="font-medium">Use Existing Job</div>
                       <div className="text-sm text-muted-foreground">Select from your saved job descriptions</div>
                     </div>
                   </Label>
                 </div>

                 {/* Show existing job selection directly under this option */}
                 {selectedUploadMethod === 'existing' && <div className="ml-12 space-y-4 p-4 bg-white rounded-lg border">
                     <div className="text-sm font-medium">Select Job Description</div>
                     {loadingJobs ? <div className="flex items-center gap-2 p-4 border rounded-lg">
                         <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                         <span className="text-sm text-muted-foreground">Loading your saved jobs...</span>
                       </div> : savedJobs.length > 0 ? <div className="space-y-2">
                          {showJobDropdown && !selectedJobId && <Select value={selectedJobId} onValueChange={handleJobSelection}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Choose from your saved job descriptions" />
                              </SelectTrigger>
                              <SelectContent className="bg-popover">
                                {savedJobs.map(job => <SelectItem key={job.id} value={job.id}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{job.jobTitle} at {job.companyName}</span>
                                      <span className="text-sm text-muted-foreground">
                                        Created {new Date(job.dateCreated).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>}
                           {selectedJobId && <div className="p-4 bg-slate-50 dark:bg-slate-950/20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-950/30 transition-colors" onClick={() => setShowJobDropdown(true)}>
                               <div className="flex items-center gap-2 mb-2">
                                 <CheckCircle className="w-5 h-5 text-slate-600" />
                                 <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Job Selected:</span>
                               </div>
                               <div className="ml-7 space-y-1">
                                 <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                   {savedJobs.find(j => j.id === selectedJobId)?.jobTitle || 'Selected Job'}
                                 </div>
                                 <div className="text-xs text-slate-700 dark:text-slate-300">
                                   Company: {savedJobs.find(j => j.id === selectedJobId)?.companyName || 'Unknown'}
                                 </div>
                                 <div className="text-xs text-slate-700 dark:text-slate-300">
                                   Created: {savedJobs.find(j => j.id === selectedJobId) ? new Date(savedJobs.find(j => j.id === selectedJobId)!.dateCreated).toLocaleDateString() : ''}
                                 </div>
                              </div>
                            </div>}
                       </div> : <div className="text-center p-8 border rounded-lg">
                         <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                         <p className="text-lg font-medium">No saved job descriptions found</p>
                         <p className="text-muted-foreground mb-4">Create your first job description using the Job Wizard</p>
                         <Button variant="outline" onClick={() => setSelectedUploadMethod('upload')}>
                           Upload New Job
                         </Button>
                       </div>}
                   </div>}
                
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'paste' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="paste" id="paste-job" className="mt-0.5" />
                  <Label htmlFor="paste-job" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'paste' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Paste Job Description</div>
                      <div className="text-sm text-muted-foreground">Copy and paste the job description text</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Show job wizard button for new job */}
              {selectedUploadMethod === 'upload' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Create New Job Description</div>
                  <div className="text-center space-y-4">
                    <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-primary/5">
                      <Target className="w-12 h-12 mx-auto mb-4 text-primary" />
                      <h3 className="text-lg font-semibold text-primary mb-2">Job Wizard</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Use our guided wizard to create a comprehensive job description from a URL or by pasting the job posting
                      </p>
                      <Button onClick={() => setShowJobWizard(true)} className="bg-primary hover:bg-primary/90">
                        <Target className="w-4 h-4 mr-2" />
                        Open Job Wizard
                      </Button>
                    </div>
                  </div>
                </div>}


              {/* Show paste textarea for paste option */}
              {selectedUploadMethod === 'paste' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Paste Job Description</div>
                  <div className="space-y-2">
                    <Label htmlFor="job-description-paste">Job Description Text</Label>
                    <Textarea id="job-description-paste" placeholder="Paste the complete job description here..." value={jobDescription} onChange={e => handleJobDescriptionChange(e.target.value)} rows={8} className="min-h-[200px]" />
                  </div>
                </div>}

                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>}

        {/* Job Assessment Questions Input - Only show for assessment-questions option */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="pb-4 bg-white/90 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-slate-200 to-slate-300/70 text-slate-700">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Step 3: Job Assessment Questions</CardTitle>
                  <CardDescription className="text-base">Upload or paste the assessment questions you need to answer</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 rounded-b-lg">
              <RadioGroup value={selectedAssessmentMethod} onValueChange={setSelectedAssessmentMethod}>
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedAssessmentMethod === 'upload' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="upload" id="upload-assessment" className="mt-0.5" />
                  <Label htmlFor="upload-assessment" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedAssessmentMethod === 'upload' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <Upload className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Upload Assessment File</div>
                      <div className="text-sm text-muted-foreground">Upload a document containing the assessment questions</div>
                    </div>
                  </Label>
                </div>

                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedAssessmentMethod === 'paste' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="paste" id="paste-assessment" className="mt-0.5" />
                  <Label htmlFor="paste-assessment" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedAssessmentMethod === 'paste' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Paste Assessment Questions</div>
                      <div className="text-sm text-muted-foreground">Copy and paste the assessment questions text</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Show upload area for upload option */}
              {selectedAssessmentMethod === 'upload' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Upload Assessment Questions</div>
                  <div className="text-center space-y-4">
                    <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-primary/5">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-primary" />
                      <h3 className="text-lg font-semibold text-primary mb-2">Upload Document</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Upload a PDF, Word document, or text file containing the assessment questions
                      </p>
                      <DocumentUpload onUploadComplete={(text, filename) => {
                          setAssessmentQuestions(text);
                          markStepComplete(4, true); // Mark step 4 as completed when questions are uploaded
                          toast({
                            title: "Assessment questions uploaded!",
                            description: "Questions extracted and ready for resume optimization."
                          });
                        }} title="Upload Assessment Questions" description="Upload the assessment questions document" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                    </div>
                  </div>
                </div>}

              {/* Show paste textarea for paste option */}
              {selectedAssessmentMethod === 'paste' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Paste Assessment Questions</div>
                  <div className="space-y-2">
                    <Label htmlFor="assessment-questions-paste">Assessment Questions</Label>
                    <Textarea id="assessment-questions-paste" placeholder="Paste all the assessment questions here. Include any pre-screening questions, application questions, or competency-based questions you need to answer..." value={assessmentQuestions} onChange={e => {
                        setAssessmentQuestions(e.target.value);
                        // Mark step 4 as completed when assessment questions are provided
                        if (e.target.value.trim()) {
                          markStepComplete(4, true);
                        } else {
                          markStepComplete(4, false);
                        }
                      }} rows={8} className="min-h-[200px]" />
                    <p className="text-xs text-muted-foreground">
                      Include all questions such as: "Why do you want this role?", "What are your salary expectations?", 
                      "Describe your relevant experience", etc.
                    </p>
                  </div>
                </div>}

              {/* Show success indicator when questions are added */}
              {assessmentQuestions.trim() && <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      Assessment questions ready ({assessmentQuestions.split('\n').filter(line => line.trim()).length} lines)
                    </span>
                  </div>
                </div>}
            </CardContent>
          </Card>}

        {/* Additional Instructions - Show for assessment questions */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="bg-white/90 rounded-t-2xl">
              <CardTitle>Step 4: Additional Instructions (Optional)</CardTitle>
              <CardDescription>Include any specific guidelines for answering the assessment questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="assessment-additional-instructions">Additional Instructions (Optional)</Label>
                <Textarea id="assessment-additional-instructions" placeholder="Include any specific tone, style, or approach preferences for addressing the assessment questions..." value={additionalInstructions} onChange={e => setAdditionalInstructions(e.target.value)} rows={4} />
              </div>
            </CardContent>
          </Card>}

        {/* Upload Additional Documents - Show for assessment questions */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="bg-white/90 rounded-t-2xl">
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Step 5: Additional Documents to Include (Optional)
              </CardTitle>
              <CardDescription>Upload supporting documents, notes, or any additional context</CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
              
              {/* Show uploaded documents */}
              {additionalDocuments.length > 0 && <div className="mt-4 space-y-2">
                  <div className="text-sm font-medium">Uploaded Documents:</div>
                  {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{doc.file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({doc.extractedText.length} characters extracted)
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>}

        {/* Step 5: Additional Instructions Question - Only show for job description */}
        {tailoringSource === 'job-description' && completedSteps.includes(4) && <Collapsible open={stepExpanded[5]} onOpenChange={() => toggleStepExpansion(5)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(5) ? (stepExpanded[5] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[5] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(5) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(5) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 5: Additional Instructions</span>
                        {completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 5 && !completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(5) && !stepExpanded[5] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {additionalInstructionsAnswer === 'yes' ? 'Additional instructions will be provided' : 'No additional instructions needed'}
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[5] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 bg-white rounded-b-lg">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 mx-[84px]">Do you want to add any additional instructions, documents or notes to help tailor your resume?</h3>
                    <p className="text-muted-foreground mb-6">This could include specific requirements, company culture notes, or additional context</p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => {
                            setAdditionalInstructionsAnswer('yes');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'yes' ? 'default' : 'outline'} className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white border-green-600">
                      Yes, I have additional instructions
                    </Button>
                     <Button onClick={() => {
                            setAdditionalInstructionsAnswer('no');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'no' ? 'default' : 'outline'} className={`px-4 py-2 text-sm ${additionalInstructionsAnswer === 'no' ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'}`}>
                      No, proceed with current information
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>}

        {/* Step 6: Additional Instructions Content (only if Step 5 = YES and NOT job-position) */}
        {tailoringSource !== 'job-position' && additionalInstructionsAnswer === 'yes' && <Collapsible open={stepExpanded[6]} onOpenChange={() => toggleStepExpansion(6)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(6) ? (stepExpanded[6] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[6] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(6) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(6) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 6: Additional Instructions & Documents</span>
                          {completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 6 && !completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                          {completedSteps.includes(6) && !stepExpanded[6] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              Additional instructions and documents saved
                            </div>}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[6] ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4 rounded-b-lg">
                <div className="space-y-2">
                  <Label htmlFor="additional-instructions-text">Additional Instructions</Label>
                  <Textarea id="additional-instructions-text" placeholder="Provide any specific guidelines, requirements, or preferences for the tailoring process..." value={additionalInstructionsText} onChange={e => setAdditionalInstructionsText(e.target.value)} rows={4} />
                </div>
                
                <div className="space-y-2">
                  <Label>Additional Documents (Optional)</Label>
                  <DocumentUpload onUploadComplete={(text, filename, fileUrl, originalFile) => {
                          if (originalFile) {
                            setAdditionalInstructionsDocuments(prev => [...prev, originalFile]);
                          }
                        }} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                </div>
                
                <div className="flex justify-center">
                  <Button onClick={() => {
                          markStepComplete(6, true);
                          toast({
                            title: "Additional instructions saved!",
                            description: "Your additional requirements have been noted."
                          });
                        }} className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Done with Additional Instructions
                  </Button>
                </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>}

        {tailoringSource === 'job-description' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptions} onOpenChange={setShowAdvancedOptions}>
              <CollapsibleTrigger asChild>
                <CardHeader className={`cursor-pointer ${showAdvancedOptions ? 'rounded-t-2xl' : 'rounded-2xl'} bg-white/90`}>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-slate-200 to-slate-300/70 text-slate-700">
                        <Settings className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your resume tailoring preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptions ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-muted/50 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[{
                            id: "feature-solution-asset",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-company",
                            checked: mentionCompanyName,
                            onChange: setMentionCompanyName,
                            title: "Company Name Integration",
                            description: "Naturally weave the target company name into your professional summary"
                          }, {
                            id: "mention-mission",
                            checked: mentionCompanyMission,
                            onChange: setMentionCompanyMission,
                            title: "Mission Alignment",
                            description: "Reference the company's mission statement to show cultural fit"
                          }, {
                            id: "mention-education",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials in the summary"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                          {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                              RECOMMENDED
                            </Badge>}
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Advanced LinkedIn Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Linkedin className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Advanced LinkedIn Options</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: "linkedin-optimization",
                            checked: false,
                            onChange: () => {
                              // Navigate to LinkedIn Wizard
                              window.open('/linkedin-wizard', '_blank');
                            },
                            title: "Get Advanced LinkedIn Optimization",
                            description: "Optimize your LinkedIn profile with our Wizard for 25x the results. Takes 3-5 minutes",
                            clickable: true
                          }, {
                            id: "linkedin-sync",
                            checked: false,
                            onChange: () => {},
                            title: "LinkedIn Automatic Sync",
                            description: "Automatically update your LinkedIn profile with tailored content",
                            comingSoon: true
                          }].map(option => <div key={option.id} className="group relative p-4 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300">
                          <div className="flex items-start space-x-3">
                             {option.clickable ? <Button size="default" className="mt-1 bg-green-600 hover:bg-green-700 px-4 py-2" onClick={() => option.onChange()}>
                                 Open LinkedIn Wizard
                               </Button> : <Checkbox id={option.id} checked={option.checked} onCheckedChange={() => option.onChange()} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />}
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {option.title}
                                </Label>
                                {option.comingSoon && <Badge variant="default" className="bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-1.5 py-0.5 font-bold shadow-sm">
                                    COMING SOON
                                  </Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience bullet points for maximum impact
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing bullet point structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={method.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={method.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Additional Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Target className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Content Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: "transferable-skills",
                            checked: transferableSkillsFocus,
                            onChange: setTransferableSkillsFocus,
                            title: "Transferable Skills Focus",
                            description: "Identify and highlight top 5 transferable hard skills and 3 key responsibilities that align with your target role"
                          }, {
                            id: "skill-relevance",
                            checked: skillRelevanceOptimization,
                            onChange: setSkillRelevanceOptimization,
                            title: "Skill Relevance Optimization",
                            description: "Remove bullet points that don't demonstrate transferable skills or target job requirements"
                          }, {
                            id: "irrelevant-filtering",
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Branding Style */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-400/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Professional Branding Style</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a branding style that matches your career level and target industry for maximum impact
                      </p>
                      <RadioGroup value={brandingStyle} onValueChange={setBrandingStyle} className="space-y-3">
                        {[{
                              value: "executive",
                              emoji: "🔹",
                              title: "Executive Branding Style",
                              description: "Elevated, authoritative, board-level tone with strategic language and leadership impact focus",
                              highlights: ["Title-heavy, 3rd person, factual structure", "No clichés or soft skills", "Enterprise-wide transformation language"],
                              bestFor: "C-suite and leadership candidates looking to position themselves as board-ready"
                            }, {
                              value: "plain-talk",
                              emoji: "🔹",
                              title: "Plain Talk Professionalism",
                              description: "Direct, simple, clear communication at a 6th–9th grade reading level",
                              highlights: ["Avoids buzzwords and abstract terms", "Focuses on actual accomplishments", "Easy to follow and trust"],
                              bestFor: "Operations, healthcare, education, customer service professionals"
                            }, {
                              value: "ats-optimized",
                              emoji: "🔹",
                              title: "ATS-Optimized Style",
                              description: "Keyword-dense, formulaic approach designed to pass Applicant Tracking Systems",
                              highlights: ["Heavy on job title keywords and tools", "No soft skills or fluff", "Compact, scannable format"],
                              bestFor: "IT, engineering, finance, logistics roles where ATS scanning is strict"
                            }, {
                              value: "story-snapshot",
                              emoji: "🔹",
                              title: "Story Snapshot Style",
                              description: "Narrative, semi-conversational tone with compelling transformation stories",
                              highlights: ["Hook in first sentence with real results", "Concrete numbers and transformation stories", "Clear positioning statement"],
                              bestFor: "Career changers, creatives, entrepreneurs with strong pivot stories"
                            }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${brandingStyle === style.value ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-purple-500/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={style.value} id={style.value} className="mt-1 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={style.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-purple-600 transition-colors flex items-center gap-2">
                                  <span>{style.emoji}</span>
                                  {style.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                  {style.description}
                                </p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-foreground">Highlights:</p>
                                  <ul className="text-xs text-muted-foreground space-y-0.5 ml-2">
                                    {style.highlights.map((highlight, index) => (
                                      <li key={index} className="flex items-start gap-1">
                                        <span className="text-purple-500 font-medium">•</span>
                                        {highlight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="mt-2 pt-2 border-t border-border/50">
                                  <p className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Best for: {style.bestFor}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Template Selection */}
                  <section className="space-y-6">
                    
                     
                     
                     {/* Template Preview Modal */}
                     {previewTemplate && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={() => setPreviewTemplate(null)}>
                         <div className="bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-auto animate-scale-in" onClick={e => e.stopPropagation()}>
                           <div className="p-6">
                             <div className="flex justify-between items-center mb-4">
                               <h3 className="text-lg font-semibold">
                                 {templateMetadata.find(t => t.id === previewTemplate)?.name}
                               </h3>
                               <button onClick={() => setPreviewTemplate(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                 <X className="w-5 h-5" />
                               </button>
                             </div>
                              <div className="scale-75 origin-top">
                                <div className="border border-gray-300 shadow-lg bg-white">
                                  {(() => {
                                    const template = templateMetadata.find(t => t.id === previewTemplate);
                                    if (!template) return null;
                                    const TemplateComponent = {
                                      Template1,
                                      Template2,
                                      Template3,
                                      Template4,
                                      Template5,
                                      Template6,
                                      Template7,
                                      Template8,
                                      Template9,
                                      Template10
                                    }[template.component as 'Template1' | 'Template2' | 'Template3' | 'Template4' | 'Template5' | 'Template6' | 'Template7' | 'Template8' | 'Template9' | 'Template10'];
                                    return <TemplateComponent data={sampleResumeData} />;
                                  })()}
                                </div>
                              </div>
                           </div>
                         </div>
                       </div>}
                  </section>

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements',
                            label: 'Job Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to your target position.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={level.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={level.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={style.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={style.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}


        {/* Step 4: Target Position Details - For job position */}
        {tailoringSource === 'job-position' && completedSteps.includes(3) && <Collapsible open={stepExpanded[4]} onOpenChange={() => toggleStepExpansion(4)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(4) ? (stepExpanded[4] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[4] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(4) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(4) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Briefcase className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 4: Target Position Details</span>
                        {completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 4 && !completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                       {completedSteps.includes(4) && !stepExpanded[4] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                           {jobPositionTitle ? `Position: ${jobPositionTitle}${careerLevel ? ` (${careerLevel})` : ''}${jobPositionIndustry ? ` - ${jobPositionIndustry}` : ''}` : 'Position details configured'}
                         </div> : <CardDescription className="text-base">
                          {completedSteps.includes(4) ? "✅ Target position configured" : "Tell us about the role you're targeting"}
                        </CardDescription>}
                    </div>
                  </div>
                   <div className="flex items-center gap-2">
                     <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[4] ? 'rotate-180' : ''}`} />
                   </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(4) || stepExpanded[4]) && <CollapsibleContent className="animate-accordion-down">
                <div className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                  
                  {/* Card 1: Job Position Title */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <Target className={`w-5 h-5 ${jobPositionTitle ? 'text-green-600' : 'text-blue-600'}`} />
                        <span className="font-bold text-slate-800">Job Position Title</span>
                        <span className="text-destructive">*</span>
                        {jobPositionTitle && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {jobPositionTitle ? `Selected: ${jobPositionTitle}` : 'What role are you applying for?'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Input 
                            id="job-position-title" 
                            placeholder="e.g., Software Engineer, Marketing Manager, Financial Analyst" 
                            value={jobPositionTitle} 
                            onChange={e => setJobPositionTitle(e.target.value)}
                            required 
                            className={`text-base py-3 px-4 rounded-xl border-2 transition-all ${jobPositionTitle ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200 shadow-sm' : 'border-gray-200 focus:border-primary focus:ring-primary/20'}`} 
                          />
                        </div>
                        
                        <div className="relative">
                          <Select value={jobPositionTitle} onValueChange={setJobPositionTitle}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${jobPositionTitle ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="💡 Or choose from comprehensive job titles list" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              {/* A-C */}
                              <SelectItem value="Account Executive">Account Executive</SelectItem>
                              <SelectItem value="Account Manager">Account Manager</SelectItem>
                              <SelectItem value="Accountant">Accountant</SelectItem>
                              <SelectItem value="Administrative Assistant">Administrative Assistant</SelectItem>
                              <SelectItem value="AI Engineer">AI Engineer</SelectItem>
                              <SelectItem value="Android Developer">Android Developer</SelectItem>
                              <SelectItem value="Architect">Architect</SelectItem>
                              <SelectItem value="Art Director">Art Director</SelectItem>
                              <SelectItem value="Auditor">Auditor</SelectItem>
                              <SelectItem value="Backend Developer">Backend Developer</SelectItem>
                              <SelectItem value="Brand Manager">Brand Manager</SelectItem>
                              <SelectItem value="Business Analyst">Business Analyst</SelectItem>
                              <SelectItem value="Business Development Manager">Business Development Manager</SelectItem>
                              <SelectItem value="CEO">CEO</SelectItem>
                              <SelectItem value="CFO">CFO</SelectItem>
                              <SelectItem value="CIO">CIO</SelectItem>
                              <SelectItem value="CMO">CMO</SelectItem>
                              <SelectItem value="COO">COO</SelectItem>
                              <SelectItem value="CTO">CTO</SelectItem>
                              <SelectItem value="Chief Data Officer">Chief Data Officer</SelectItem>
                              <SelectItem value="Chief Product Officer">Chief Product Officer</SelectItem>
                              <SelectItem value="Civil Engineer">Civil Engineer</SelectItem>
                              <SelectItem value="Cloud Architect">Cloud Architect</SelectItem>
                              <SelectItem value="Cloud Engineer">Cloud Engineer</SelectItem>
                              <SelectItem value="Community Manager">Community Manager</SelectItem>
                              <SelectItem value="Compliance Manager">Compliance Manager</SelectItem>
                              <SelectItem value="Consultant">Consultant</SelectItem>
                              <SelectItem value="Content Creator">Content Creator</SelectItem>
                              <SelectItem value="Content Manager">Content Manager</SelectItem>
                              <SelectItem value="Content Strategist">Content Strategist</SelectItem>
                              <SelectItem value="Creative Director">Creative Director</SelectItem>
                              <SelectItem value="Customer Success Manager">Customer Success Manager</SelectItem>
                              <SelectItem value="Customer Support Representative">Customer Support Representative</SelectItem>
                              <SelectItem value="Cybersecurity Analyst">Cybersecurity Analyst</SelectItem>
                              <SelectItem value="Cybersecurity Engineer">Cybersecurity Engineer</SelectItem>
                              
                              {/* D-F */}
                              <SelectItem value="Data Analyst">Data Analyst</SelectItem>
                              <SelectItem value="Data Engineer">Data Engineer</SelectItem>
                              <SelectItem value="Data Scientist">Data Scientist</SelectItem>
                              <SelectItem value="Database Administrator">Database Administrator</SelectItem>
                              <SelectItem value="Dentist">Dentist</SelectItem>
                              <SelectItem value="DevOps Engineer">DevOps Engineer</SelectItem>
                              <SelectItem value="Digital Marketing Manager">Digital Marketing Manager</SelectItem>
                              <SelectItem value="Digital Marketing Specialist">Digital Marketing Specialist</SelectItem>
                              <SelectItem value="Director of Engineering">Director of Engineering</SelectItem>
                              <SelectItem value="Director of Marketing">Director of Marketing</SelectItem>
                              <SelectItem value="Director of Operations">Director of Operations</SelectItem>
                              <SelectItem value="Director of Product">Director of Product</SelectItem>
                              <SelectItem value="Director of Sales">Director of Sales</SelectItem>
                              <SelectItem value="Doctor">Doctor</SelectItem>
                              <SelectItem value="Electrical Engineer">Electrical Engineer</SelectItem>
                              <SelectItem value="Event Coordinator">Event Coordinator</SelectItem>
                              <SelectItem value="Event Manager">Event Manager</SelectItem>
                              <SelectItem value="Executive Assistant">Executive Assistant</SelectItem>
                              <SelectItem value="Financial Advisor">Financial Advisor</SelectItem>
                              <SelectItem value="Financial Analyst">Financial Analyst</SelectItem>
                              <SelectItem value="Financial Planner">Financial Planner</SelectItem>
                              <SelectItem value="Frontend Developer">Frontend Developer</SelectItem>
                              <SelectItem value="Full Stack Developer">Full Stack Developer</SelectItem>
                              
                              {/* G-L */}
                              <SelectItem value="Graphic Designer">Graphic Designer</SelectItem>
                              <SelectItem value="Growth Manager">Growth Manager</SelectItem>
                              <SelectItem value="HR Business Partner">HR Business Partner</SelectItem>
                              <SelectItem value="HR Generalist">HR Generalist</SelectItem>
                              <SelectItem value="HR Manager">HR Manager</SelectItem>
                              <SelectItem value="Human Resources Director">Human Resources Director</SelectItem>
                              <SelectItem value="Human Resources Manager">Human Resources Manager</SelectItem>
                              <SelectItem value="iOS Developer">iOS Developer</SelectItem>
                              <SelectItem value="IT Manager">IT Manager</SelectItem>
                              <SelectItem value="IT Support Specialist">IT Support Specialist</SelectItem>
                              <SelectItem value="Investment Banker">Investment Banker</SelectItem>
                              <SelectItem value="Journalist">Journalist</SelectItem>
                              <SelectItem value="Lawyer">Lawyer</SelectItem>
                              <SelectItem value="Lead Developer">Lead Developer</SelectItem>
                              <SelectItem value="Legal Counsel">Legal Counsel</SelectItem>
                              <SelectItem value="Librarian">Librarian</SelectItem>
                              <SelectItem value="Machine Learning Engineer">Machine Learning Engineer</SelectItem>
                              
                              {/* M-P */}
                              <SelectItem value="Management Consultant">Management Consultant</SelectItem>
                              <SelectItem value="Marketing Coordinator">Marketing Coordinator</SelectItem>
                              <SelectItem value="Marketing Director">Marketing Director</SelectItem>
                              <SelectItem value="Marketing Manager">Marketing Manager</SelectItem>
                              <SelectItem value="Marketing Specialist">Marketing Specialist</SelectItem>
                              <SelectItem value="Mechanical Engineer">Mechanical Engineer</SelectItem>
                              <SelectItem value="Mobile App Developer">Mobile App Developer</SelectItem>
                              <SelectItem value="Network Administrator">Network Administrator</SelectItem>
                              <SelectItem value="Network Engineer">Network Engineer</SelectItem>
                              <SelectItem value="Nurse">Nurse</SelectItem>
                              <SelectItem value="Nurse Practitioner">Nurse Practitioner</SelectItem>
                              <SelectItem value="Operations Analyst">Operations Analyst</SelectItem>
                              <SelectItem value="Operations Director">Operations Director</SelectItem>
                              <SelectItem value="Operations Manager">Operations Manager</SelectItem>
                              <SelectItem value="Paralegal">Paralegal</SelectItem>
                              <SelectItem value="Pharmacist">Pharmacist</SelectItem>
                              <SelectItem value="Physical Therapist">Physical Therapist</SelectItem>
                              <SelectItem value="Physician">Physician</SelectItem>
                              <SelectItem value="Physician Assistant">Physician Assistant</SelectItem>
                              <SelectItem value="Principal Engineer">Principal Engineer</SelectItem>
                              <SelectItem value="Product Designer">Product Designer</SelectItem>
                              <SelectItem value="Product Manager">Product Manager</SelectItem>
                              <SelectItem value="Product Marketing Manager">Product Marketing Manager</SelectItem>
                              <SelectItem value="Product Owner">Product Owner</SelectItem>
                              <SelectItem value="Program Manager">Program Manager</SelectItem>
                              <SelectItem value="Project Coordinator">Project Coordinator</SelectItem>
                              <SelectItem value="Project Manager">Project Manager</SelectItem>
                              <SelectItem value="Public Relations Manager">Public Relations Manager</SelectItem>
                              <SelectItem value="Psychologist">Psychologist</SelectItem>
                              
                              {/* Q-S */}
                              <SelectItem value="QA Engineer">QA Engineer</SelectItem>
                              <SelectItem value="Quality Assurance Manager">Quality Assurance Manager</SelectItem>
                              <SelectItem value="Radiologist">Radiologist</SelectItem>
                              <SelectItem value="Real Estate Agent">Real Estate Agent</SelectItem>
                              <SelectItem value="Recruiter">Recruiter</SelectItem>
                              <SelectItem value="Regional Manager">Regional Manager</SelectItem>
                              <SelectItem value="Research Analyst">Research Analyst</SelectItem>
                              <SelectItem value="Research Scientist">Research Scientist</SelectItem>
                              <SelectItem value="Sales Director">Sales Director</SelectItem>
                              <SelectItem value="Sales Engineer">Sales Engineer</SelectItem>
                              <SelectItem value="Sales Manager">Sales Manager</SelectItem>
                              <SelectItem value="Sales Representative">Sales Representative</SelectItem>
                              <SelectItem value="Scrum Master">Scrum Master</SelectItem>
                              <SelectItem value="Security Analyst">Security Analyst</SelectItem>
                              <SelectItem value="Security Engineer">Security Engineer</SelectItem>
                              <SelectItem value="SEO Specialist">SEO Specialist</SelectItem>
                              <SelectItem value="Social Media Manager">Social Media Manager</SelectItem>
                              <SelectItem value="Social Worker">Social Worker</SelectItem>
                              <SelectItem value="Software Architect">Software Architect</SelectItem>
                              <SelectItem value="Software Developer">Software Developer</SelectItem>
                              <SelectItem value="Software Engineer">Software Engineer</SelectItem>
                              <SelectItem value="Solutions Architect">Solutions Architect</SelectItem>
                              <SelectItem value="Specialist">Specialist</SelectItem>
                              <SelectItem value="Supply Chain Manager">Supply Chain Manager</SelectItem>
                              <SelectItem value="Surgeon">Surgeon</SelectItem>
                              <SelectItem value="Systems Administrator">Systems Administrator</SelectItem>
                              <SelectItem value="Systems Analyst">Systems Analyst</SelectItem>
                              <SelectItem value="Systems Engineer">Systems Engineer</SelectItem>
                              
                              {/* T-Z */}
                              <SelectItem value="Tax Advisor">Tax Advisor</SelectItem>
                              <SelectItem value="Teacher">Teacher</SelectItem>
                              <SelectItem value="Technical Lead">Technical Lead</SelectItem>
                              <SelectItem value="Technical Writer">Technical Writer</SelectItem>
                              <SelectItem value="Territory Manager">Territory Manager</SelectItem>
                              <SelectItem value="Test Engineer">Test Engineer</SelectItem>
                              <SelectItem value="Therapist">Therapist</SelectItem>
                              <SelectItem value="Training Manager">Training Manager</SelectItem>
                              <SelectItem value="UI/UX Designer">UI/UX Designer</SelectItem>
                              <SelectItem value="UX Designer">UX Designer</SelectItem>
                              <SelectItem value="UX Researcher">UX Researcher</SelectItem>
                              <SelectItem value="Vice President">Vice President</SelectItem>
                              <SelectItem value="Vice President of Engineering">Vice President of Engineering</SelectItem>
                              <SelectItem value="Vice President of Marketing">Vice President of Marketing</SelectItem>
                              <SelectItem value="Vice President of Sales">Vice President of Sales</SelectItem>
                              <SelectItem value="Web Developer">Web Developer</SelectItem>
                              <SelectItem value="Web Designer">Web Designer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card 2: Career Level */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-purple-50 to-pink-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <GraduationCap className={`w-5 h-5 ${careerLevel ? 'text-green-600' : 'text-purple-600'}`} />
                        <span className="font-bold text-slate-800">Career Level</span>
                        <Badge variant="secondary" className="ml-2">Optional</Badge>
                        {careerLevel && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {careerLevel ? `Selected: ${careerLevel}` : 'What level is this position? This helps match expectations.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Select value={careerLevel} onValueChange={setCareerLevel}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${careerLevel ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="Select career level" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              <SelectItem value="Intern">Intern</SelectItem>
                              <SelectItem value="Entry Level">Entry Level</SelectItem>
                              <SelectItem value="Level I">Level I</SelectItem>
                              <SelectItem value="Junior">Junior</SelectItem>
                              <SelectItem value="Level II">Level II</SelectItem>
                              <SelectItem value="Associate">Associate</SelectItem>
                              <SelectItem value="Mid-Level">Mid-Level</SelectItem>
                              <SelectItem value="Level III">Level III</SelectItem>
                              <SelectItem value="Senior">Senior</SelectItem>
                              <SelectItem value="Level IV">Level IV</SelectItem>
                              <SelectItem value="Staff">Staff</SelectItem>
                              <SelectItem value="Level V">Level V</SelectItem>
                              <SelectItem value="Principal">Principal</SelectItem>
                              <SelectItem value="Lead">Lead</SelectItem>
                              <SelectItem value="Specialist">Specialist</SelectItem>
                              <SelectItem value="Consultant">Consultant</SelectItem>
                              <SelectItem value="Manager">Manager</SelectItem>
                              <SelectItem value="Senior Manager">Senior Manager</SelectItem>
                              <SelectItem value="Director">Director</SelectItem>
                              <SelectItem value="Senior Director">Senior Director</SelectItem>
                              <SelectItem value="Vice President">Vice President</SelectItem>
                              <SelectItem value="Senior Vice President">Senior Vice President</SelectItem>
                              <SelectItem value="Executive Vice President">Executive Vice President</SelectItem>
                              <SelectItem value="C-Level (CEO, CTO, CFO, etc.)">C-Level (CEO, CTO, CFO, etc.)</SelectItem>
                              <SelectItem value="President">President</SelectItem>
                              <SelectItem value="Founder">Founder</SelectItem>
                              <SelectItem value="Co-Founder">Co-Founder</SelectItem>
                              <SelectItem value="Partner">Partner</SelectItem>
                              <SelectItem value="Managing Partner">Managing Partner</SelectItem>
                              <SelectItem value="Board Member">Board Member</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card 3: Industry */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-green-50 to-teal-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${jobPositionIndustry ? 'text-green-600' : 'text-green-600'}`} />
                        <span className="font-bold text-slate-800">Industry</span>
                        <Badge variant="secondary" className="ml-2">Optional</Badge>
                        {jobPositionIndustry && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {jobPositionIndustry ? `Selected: ${jobPositionIndustry}` : 'Which industry is this role in? This helps us tailor more specifically.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Input 
                            id="job-position-industry" 
                            placeholder="e.g., Technology, Healthcare, Finance, Education" 
                            value={jobPositionIndustry} 
                            onChange={e => setJobPositionIndustry(e.target.value)} 
                            className={`text-base py-3 px-4 rounded-xl border-2 transition-all ${jobPositionIndustry ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200 shadow-sm' : 'border-gray-200 focus:border-primary focus:ring-primary/20'}`} 
                          />
                        </div>
                        
                        <div className="relative">
                          <Select value={jobPositionIndustry} onValueChange={setJobPositionIndustry}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${jobPositionIndustry ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="💼 Or select from common industries" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              <SelectItem value="Accounting">Accounting</SelectItem>
                              <SelectItem value="Advertising">Advertising</SelectItem>
                              <SelectItem value="Aerospace">Aerospace</SelectItem>
                              <SelectItem value="Agriculture">Agriculture</SelectItem>
                              <SelectItem value="Architecture">Architecture</SelectItem>
                              <SelectItem value="Arts & Design">Arts & Design</SelectItem>
                              <SelectItem value="Automotive">Automotive</SelectItem>
                              <SelectItem value="Aviation">Aviation</SelectItem>
                              <SelectItem value="Banking">Banking</SelectItem>
                              <SelectItem value="Biotechnology">Biotechnology</SelectItem>
                              <SelectItem value="Broadcasting">Broadcasting</SelectItem>
                              <SelectItem value="Business Services">Business Services</SelectItem>
                              <SelectItem value="Chemical">Chemical</SelectItem>
                              <SelectItem value="Communications">Communications</SelectItem>
                              <SelectItem value="Computer Software">Computer Software</SelectItem>
                              <SelectItem value="Construction">Construction</SelectItem>
                              <SelectItem value="Consulting">Consulting</SelectItem>
                              <SelectItem value="Consumer Goods">Consumer Goods</SelectItem>
                              <SelectItem value="Cybersecurity">Cybersecurity</SelectItem>
                              <SelectItem value="Data Analytics">Data Analytics</SelectItem>
                              <SelectItem value="Defense">Defense</SelectItem>
                              <SelectItem value="E-commerce">E-commerce</SelectItem>
                              <SelectItem value="Education">Education</SelectItem>
                              <SelectItem value="Electronics">Electronics</SelectItem>
                              <SelectItem value="Energy">Energy</SelectItem>
                              <SelectItem value="Engineering">Engineering</SelectItem>
                              <SelectItem value="Entertainment">Entertainment</SelectItem>
                              <SelectItem value="Environmental Services">Environmental Services</SelectItem>
                              <SelectItem value="Fashion">Fashion</SelectItem>
                              <SelectItem value="Finance">Finance</SelectItem>
                              <SelectItem value="Financial Services">Financial Services</SelectItem>
                              <SelectItem value="Food & Beverage">Food & Beverage</SelectItem>
                              <SelectItem value="Gaming">Gaming</SelectItem>
                              <SelectItem value="Government">Government</SelectItem>
                              <SelectItem value="Healthcare">Healthcare</SelectItem>
                              <SelectItem value="Hospitality">Hospitality</SelectItem>
                              <SelectItem value="Human Resources">Human Resources</SelectItem>
                              <SelectItem value="Information Technology">Information Technology</SelectItem>
                              <SelectItem value="Insurance">Insurance</SelectItem>
                              <SelectItem value="Internet">Internet</SelectItem>
                              <SelectItem value="Investment Banking">Investment Banking</SelectItem>
                              <SelectItem value="Legal Services">Legal Services</SelectItem>
                              <SelectItem value="Logistics">Logistics</SelectItem>
                              <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="Marketing">Marketing</SelectItem>
                              <SelectItem value="Media">Media</SelectItem>
                              <SelectItem value="Medical Devices">Medical Devices</SelectItem>
                              <SelectItem value="Mining">Mining</SelectItem>
                              <SelectItem value="Nonprofit">Nonprofit</SelectItem>
                              <SelectItem value="Oil & Gas">Oil & Gas</SelectItem>
                              <SelectItem value="Pharmaceuticals">Pharmaceuticals</SelectItem>
                              <SelectItem value="Public Relations">Public Relations</SelectItem>
                              <SelectItem value="Publishing">Publishing</SelectItem>
                              <SelectItem value="Real Estate">Real Estate</SelectItem>
                              <SelectItem value="Renewable Energy">Renewable Energy</SelectItem>
                              <SelectItem value="Research">Research</SelectItem>
                              <SelectItem value="Retail">Retail</SelectItem>
                              <SelectItem value="Sales">Sales</SelectItem>
                              <SelectItem value="Security">Security</SelectItem>
                              <SelectItem value="Semiconductors">Semiconductors</SelectItem>
                              <SelectItem value="Social Media">Social Media</SelectItem>
                              <SelectItem value="Software">Software</SelectItem>
                              <SelectItem value="Sports">Sports</SelectItem>
                              <SelectItem value="Staffing & Recruiting">Staffing & Recruiting</SelectItem>
                              <SelectItem value="Supply Chain">Supply Chain</SelectItem>
                              <SelectItem value="Technology">Technology</SelectItem>
                              <SelectItem value="Telecommunications">Telecommunications</SelectItem>
                              <SelectItem value="Tourism">Tourism</SelectItem>
                              <SelectItem value="Transportation">Transportation</SelectItem>
                              <SelectItem value="Utilities">Utilities</SelectItem>
                              <SelectItem value="Venture Capital">Venture Capital</SelectItem>
                              <SelectItem value="Wellness">Wellness</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Tailoring Options */}
                {(jobPositionTitle || jobPositionIndustry) && <div className="space-y-4 pt-4 border-t border-border max-w-xl mx-auto mb-6">
                  {/* Basic Tailoring Option */}
                  <Card className={`border-2 transition-all cursor-pointer relative shadow-lg ${selectedTailoringType === 'basic' ? 'border-green-500 bg-gradient-to-br from-green-100 to-green-200 shadow-xl ring-4 ring-green-200' : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200'}`} onClick={() => setSelectedTailoringType('basic')}>
                    {selectedTailoringType === 'basic' && <div className="absolute top-3 right-3">
                        <Badge className="bg-green-600 text-white border-0">SELECTED</Badge>
                      </div>}
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${selectedTailoringType === 'basic' ? 'bg-green-600 border-green-600' : 'bg-blue-500 border-blue-300'}`}>
                            {selectedTailoringType === 'basic' ? <CheckCircle className="w-5 h-5 text-white" /> : <TrendingUp className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <CardTitle className={`text-lg ${selectedTailoringType === 'basic' ? 'text-green-900 font-bold' : 'text-blue-800'}`}>
                              Basic Tailoring
                            </CardTitle>
                            <p className={`text-sm font-medium ${selectedTailoringType === 'basic' ? 'text-green-700' : 'text-blue-700'}`}>Under 45 Seconds</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className={`${selectedTailoringType === 'basic' ? 'bg-green-600' : 'bg-blue-600'} text-white`}>FAST</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 rounded-b-lg">
                      <p className={`text-sm ${selectedTailoringType === 'basic' ? 'text-green-800' : 'text-blue-800'}`}>
                        Instantly tailor your resume using our AI analysis of your target {jobPositionTitle} role{jobPositionIndustry && ` in ${jobPositionIndustry}`}. 
                        We'll optimize key sections based on industry standards and common requirements.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Advanced Tailoring Option */}
                  <Card className={`border-2 transition-all cursor-pointer relative shadow-lg ${selectedTailoringType === 'advanced' ? 'border-green-500 bg-gradient-to-br from-green-100 to-green-200 shadow-xl ring-4 ring-green-200' : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200'}`} onClick={() => setSelectedTailoringType('advanced')}>
                    {selectedTailoringType === 'advanced' && <div className="absolute top-3 right-3">
                        <Badge className="bg-green-600 text-white border-0">SELECTED</Badge>
                      </div>}
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${selectedTailoringType === 'advanced' ? 'bg-green-600 border-green-600' : 'bg-green-500 border-green-300'}`}>
                            {selectedTailoringType === 'advanced' ? <CheckCircle className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <CardTitle className={`text-lg ${selectedTailoringType === 'advanced' ? 'text-green-900 font-bold' : 'text-green-800'}`}>
                              Advanced Tailoring
                            </CardTitle>
                            <p className="text-sm text-green-700 font-medium">2-3 Minutes • 15x More Accurate</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-green-600 text-white">BEST RESULTS</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 rounded-b-lg">
                      <p className="text-sm text-green-800">
                        Get maximum precision by answering a few targeted questions about your experience and goals. 
                        This creates a highly customized resume that's 15x more likely to get you noticed by hiring managers and ATS systems.
                      </p>
                       {selectedTailoringType === 'advanced' && <div className="mt-4 pt-4 border-t border-green-200 space-y-4">
                           <div>
                              <Label className="text-sm font-medium text-green-800">Load Saved Analysis (Optional)</Label>
                              <Select value={selectedSavedAnalysisId} onValueChange={handleSavedAnalysisSelection} onOpenChange={open => {
                              if (open && savedJobAnalyses.length === 0) {
                                loadSavedJobAnalyses();
                              }
                            }}>
                                <SelectTrigger className="mt-1 bg-background border shadow-sm z-50">
                                  <SelectValue placeholder="Select a saved analysis or create new" />
                                </SelectTrigger>
                                <SelectContent className="bg-background border shadow-lg z-50">
                                  <SelectItem value="clear">
                                    <div className="flex items-center gap-2">
                                      <span className="text-green-600">✨</span>
                                      <span className="font-medium">Create New Analysis</span>
                                    </div>
                                  </SelectItem>
                                  {loadingSavedAnalyses ? <SelectItem value="loading" disabled>Loading...</SelectItem> : savedJobAnalyses.length > 0 ? <>
                                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
                                        Saved Analyses
                                      </div>
                                      {savedJobAnalyses.map(analysis => <SelectItem key={analysis.id} value={analysis.id}>
                                          <div className="flex items-center gap-2">
                                            <span className="text-blue-600">📋</span>
                                            <span>{analysis.position_display_name}</span>
                                          </div>
                                        </SelectItem>)}
                                    </> : <SelectItem value="none" disabled>No saved analyses found</SelectItem>}
                                </SelectContent>
                              </Select>
                           </div>
                            {/* Progress Bar for Question Generation */}
                            {loadingAnalysis && <Card className="p-4 mb-4 bg-green-50 border-green-200">
                                <div className="text-center space-y-3">
                                  <div className="flex items-center justify-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                                    <span className="text-sm font-medium text-green-800">
                                      Generating Strategic Questions... {Math.round(analysisProgress)}%
                                    </span>
                                  </div>
                                  
                                  <div className="text-xs text-green-700">
                                    {analysisProgress < 30 ? "Analyzing job requirements..." : analysisProgress < 60 ? "Generating strategic questions..." : analysisProgress < 85 ? "Tailoring questions to role..." : analysisProgress < 100 ? "Finalizing analysis..." : "Complete!"}
                                  </div>
                                </div>
                              </Card>}

                            <Button onClick={async e => {
                            e.stopPropagation();
                            if (selectedSavedAnalysisId) {
                              // Use saved analysis - no need to generate new one
                              setShowAdvancedWizard(true);
                            } else {
                              // Generate new analysis
                              await generateJobAnalysis();
                              setShowAdvancedWizard(true);
                            }
                          }} className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={!jobPositionTitle || loadingAnalysis}>
                              {loadingAnalysis ? 'Analyzing Job...' : selectedSavedAnalysisId && selectedSavedAnalysisId !== '' && selectedSavedAnalysisId !== 'none' ? 'Use Saved Analysis' : 'Answer Questions Now'}
                            </Button>
                         </div>}
                    </CardContent>
                  </Card>
                </div>}
                
                {/* Finished Selecting Position Details Button */}
                {!completedSteps.includes(4) && <div className="mt-8 pt-6 pb-8 border-t border-gray-200/50">
                  <div className="flex justify-center">
                    <Button onClick={() => {
                        if (!jobPositionTitle.trim()) {
                          toast({
                            title: "Position Title Required",
                            description: "Please enter a job position title to continue.",
                            variant: "destructive"
                          });
                          return;
                        }
                        setStep4Completed(true);
                        markStepComplete(4, true);
                        toast({
                          title: "Position details completed!",
                          description: `Target position: ${jobPositionTitle} configured successfully.`
                        });
                      }} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-md" disabled={!jobPositionTitle.trim()}>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Finished Selecting Position Details
                    </Button>
                  </div>
                </div>}
              </CollapsibleContent>}
          </Card>
        </Collapsible>}

        {/* Step 5: Additional Instructions Question - For job position */}
        {tailoringSource === 'job-position' && completedSteps.includes(4) && <Collapsible open={stepExpanded[5]} onOpenChange={() => toggleStepExpansion(5)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(5) ? (stepExpanded[5] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[5] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(5) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(5) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 5: Additional Instructions</span>
                        {completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 5 && !completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(5) && !stepExpanded[5] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {additionalInstructionsAnswer === 'yes' ? 'Additional instructions will be provided' : 'No additional instructions needed'}
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[5] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 bg-white rounded-b-lg">
                <div className="space-y-6 pt-8 px-4 pb-8">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 mx-[84px]">Do you want to add any additional instructions, documents or notes to help tailor your resume?</h3>
                    <p className="text-muted-foreground mb-6">This could include specific requirements, company culture notes, or additional context</p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => {
                            setAdditionalInstructionsAnswer('yes');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'yes' ? 'default' : 'outline'} className="px-6 py-3 text-sm bg-green-600 hover:bg-green-700 text-white border-green-600 rounded-xl transition-all hover:scale-105 shadow-md">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Yes, I have additional instructions
                    </Button>
                     <Button onClick={() => {
                            setAdditionalInstructionsAnswer('no');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'no' ? 'default' : 'outline'} className={`px-6 py-3 text-sm rounded-xl transition-all hover:scale-105 shadow-md ${additionalInstructionsAnswer === 'no' ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'}`}>
                      <X className="w-4 h-4 mr-2" />
                      No, proceed with current information
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>}

        {/* Step 6: Additional Instructions & Documents Content (only if Step 5 = YES) - For job position */}
        {tailoringSource === 'job-position' && additionalInstructionsAnswer === 'yes' && <Collapsible open={stepExpanded[6]} onOpenChange={() => toggleStepExpansion(6)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(6) ? (stepExpanded[6] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[6] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(6) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(6) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 6: Additional Instructions & Docs</span>
                        {completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 6 && !completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(6) && !stepExpanded[6] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Additional instructions and documents saved
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[6] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(6) || stepExpanded[6]) && <CollapsibleContent className="animate-accordion-down">
                <CardContent className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                  {/* Additional Instructions Section */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="additional-instructions" className="text-base font-semibold">Additional Instructions</Label>
                      <p className="text-sm text-muted-foreground mt-1">Provide any specific guidelines, requirements, or preferences for tailoring</p>
                    </div>
                    <Textarea id="additional-instructions" placeholder="Include any specific guidelines, requirements, or preferences for the tailoring process..." value={additionalInstructions} onChange={e => {
                        setAdditionalInstructions(e.target.value);
                      }} rows={4} />
                  </div>

                  <div className="border-t border-border pt-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Additional Instructions & Docs</Label>
                        <p className="text-sm text-muted-foreground mt-1">Upload any additional documents, notes, or context files</p>
                      </div>
                      
                      <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                      
                      {additionalDocuments.length > 0 && <div className="space-y-2">
                          <Label className="text-sm font-medium">Uploaded Documents ({additionalDocuments.length}):</Label>
                          {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2">
                                 <FileText className="w-4 h-4 text-slate-600" />
                                <span className="text-sm font-medium">{doc.file.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({doc.extractedText.length} characters extracted)
                                </span>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>)}
                        </div>}
                    </div>
                  </div>
                  
                  {/* Complete step button */}
                  {!completedSteps.includes(6) && <div className="flex justify-center pt-4">
                    <Button onClick={() => {
                        setStep6Completed(true);
                        markStepComplete(6, true);
                        toast({
                          title: "Additional instructions completed!",
                          description: "Your additional instructions and documents have been saved."
                        });
                      }} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Additional Instructions
                    </Button>
                  </div>}
                </CardContent>
              </CollapsibleContent>}
          </Card>
        </Collapsible>}

        {/* Advanced Tailoring Options - For job position */}
        {tailoringSource === 'job-position' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptionsJob} onOpenChange={setShowAdvancedOptionsJob}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer bg-white/90 rounded-t-2xl border-b border-gray-200">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your resume tailoring preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptionsJob ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options - Hidden for Job Position/Industry tailoring */}
                  {tailoringSource !== 'job-position' && <section className="space-y-6">
                      <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {[{
                            id: "feature-solution-asset-pos",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-company-pos",
                            checked: mentionCompanyName,
                            onChange: setMentionCompanyName,
                            title: "Company Name Integration",
                            description: "Naturally weave the target company name into your professional summary"
                          }, {
                            id: "mention-mission-pos",
                            checked: mentionCompanyMission,
                            onChange: setMentionCompanyMission,
                            title: "Mission Alignment",
                            description: "Reference the company's mission statement to show cultural fit"
                          }, {
                            id: "mention-education-pos",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials in the summary"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                            {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                                RECOMMENDED
                              </Badge>}
                            <div className="flex items-start space-x-3">
                               <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {option.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {option.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </section>}

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience bullet points for maximum impact
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing bullet point structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={`method-pos-${method.value}`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={`method-pos-${method.value}`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'irrelevant-filtering-pos',
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Template Selection - Hidden for Job Position/Industry tailoring */}
                  {tailoringSource !== 'job-position' && <section className="space-y-6">
                      <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Template Selection</h3>
                      </div>
                       <RadioGroup value={selectedTemplateMain} onValueChange={setSelectedTemplateMain}>
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                           {templateMetadata.map(template => <div key={template.id} className="group relative">
                               <div className="border border-border rounded-lg p-4 bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300 cursor-pointer">
                                 <div className="flex items-center space-x-2 mb-3">
                                   <RadioGroupItem value={template.id} id={`${template.id}-pos`} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                                   <Label htmlFor={`${template.id}-pos`} className="text-sm font-semibold cursor-pointer group-hover:text-primary transition-colors">
                                     {template.name}
                                   </Label>
                                 </div>
                                 <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{template.description}</p>
                                 <div className="flex flex-wrap gap-2 mb-3">
                                   {template.features.map((feature, idx) => <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                                       {feature}
                                     </span>)}
                                 </div>
                                 <button onClick={() => setPreviewTemplate(template.id)} className="text-xs text-primary hover:text-primary/80 transition-colors font-medium">
                                   Preview Template →
                                 </button>
                               </div>
                             </div>)}
                         </div>
                       </RadioGroup>
                    </section>}

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-pos-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-pos-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                        <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition-pos',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian-pos',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements-pos',
                            label: 'Job Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to your target position.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={`${level.value}-pos`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${level.value}-pos`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={`${style.value}-pos`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${style.value}-pos`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}

        {/* Select Documents to Tailor - For institution (first) */}
        {tailoringSource === 'institution' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 3: Select Documents to Tailor</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Choose which professional documents you want to tailor for this institution
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 rounded-b-lg">
              {/* Professional Documents Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary" />
                    Professional Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document',
                          category: 'professional'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter',
                          category: 'professional'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Professional social media',
                          category: 'professional'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio',
                          category: 'professional'
                        }, {
                          id: 'institutional-mission-alignment',
                          label: 'Institutional Mission Alignment',
                          icon: Target,
                          description: 'Values and mission compatibility document',
                          category: 'professional'
                        }, {
                          id: 'organizational-fit-assessment',
                          label: 'Organizational Fit Assessment',
                          icon: Users,
                          description: 'Cultural and organizational compatibility',
                          category: 'professional'
                        }, {
                          id: 'institutional-impact-statement',
                          label: 'Institutional Impact Statement',
                          icon: BarChart3,
                          description: 'Potential contribution and impact analysis',
                          category: 'professional'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          return <div key={document.id} className={`group relative p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                           {['institutional-mission-alignment', 'organizational-fit-assessment', 'institutional-impact-statement'].includes(document.id) && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-2 py-1 font-bold shadow-md">
                               NEW
                             </Badge>}
                          <div className="flex items-start space-x-3">
                               <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                             <div className="flex-1 space-y-2">
                               <div className="flex items-center gap-2">
                                 <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-metallic-foreground' : 'text-muted-foreground group-hover:text-metallic-dark'}`} />
                                 <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-metallic-foreground' : 'text-foreground group-hover:text-metallic-dark'}`}>
                                  {document.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                        })}
                  </div>
                </div>

                {/* Academic Documents Section */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" />
                    Academic & Institutional Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[{
                          id: 'admissions-letter',
                          label: 'Admissions Letter',
                          icon: GraduationCap,
                          description: 'For academic or training institutions',
                          category: 'academic'
                        }, {
                          id: 'statement-of-intent',
                          label: 'Statement of Intent',
                          icon: Target,
                          description: 'Purpose and goals statement',
                          category: 'academic'
                        }, {
                          id: 'personal-mission',
                          label: 'Personal Mission Statement',
                          icon: Heart,
                          description: 'Values and mission alignment',
                          category: 'academic'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          return <div key={document.id} className={`group p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                          <div className="flex items-start space-x-3">
                               <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                                <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                  {document.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                        })}
                  </div>
                </div>

                {/* Communication Documents Section */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Communication Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[{
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-meeting follow-up',
                          category: 'communication'
                        }, {
                          id: 'outreach-email',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Introduction and networking',
                          category: 'communication'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          return <div key={document.id} className={`group p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                                <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                  {document.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                        })}
                  </div>
                </div>
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for institutional tailoring
                    </span>
                  </div>
                </div>}
            </CardContent>
          </Card>}

        {/* Institution/Organization Details - Only show for institution option */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Step 4: Institution/Organization Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 rounded-b-lg">
              <div className="space-y-2">
                <Label htmlFor="institution-name">Institution/Organization Name *</Label>
                <Input id="institution-name" placeholder="Enter institution or organization name" value={institutionName} onChange={e => {
                      setInstitutionName(e.target.value);
                      // Mark step 4 as completed when institution name is provided
                      if (e.target.value.trim()) {
                        setStep4Completed(true);
                      } else {
                        setStep4Completed(false);
                      }
                    }} required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="institution-type">Institution Type</Label>
                <Select value={institutionType} onValueChange={setInstitutionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select institution type (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="academic">Academic</SelectItem>
                    <SelectItem value="nonprofit">Nonprofit</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="religious">Religious</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mission-alignment">Mission Alignment Statement (Optional)</Label>
                <Textarea id="mission-alignment" placeholder="Summarize in 1–2 sentences how your goals or values align with this institution's mission..." value={missionAlignment} onChange={e => setMissionAlignment(e.target.value)} rows={3} />
              </div>
            </CardContent>
          </Card>}


        {/* Institution-specific Additional Instructions - After document selection */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle>Step 5: Institutional Guidelines & Alignment</CardTitle>
              <CardDescription>Please provide any specific guidelines or expectations the institution has for this document</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institutional-guidelines">Institutional Guidelines</Label>
                  <Textarea id="institutional-guidelines" placeholder="This may include admissions requirements, personal values the institution emphasizes, tone of voice they prefer (formal, mission-driven, faith-centered, etc.), or specific experiences or themes you've been asked to include..." value={additionalInstructions} onChange={e => setAdditionalInstructions(e.target.value)} rows={4} />
                </div>
                
                {selectedDocuments.some(doc => ['executive-bio', 'statement-of-intent'].includes(doc)) && <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">For Executive Bio or Statement of Intent:</h4>
                    <p className="text-sm text-muted-foreground">
                      Are there any particular achievements, themes, or beliefs you want to highlight that align with this institution's values, mission, or impact areas?
                    </p>
                  </div>}
              </div>
            </CardContent>
          </Card>}


        {/* Upload Additional Documents - Show for institution (last) */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Step 6: Upload Additional Documents or Notes (Optional)
              </CardTitle>
              <CardDescription>Upload supporting documents, notes, or any additional context</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 rounded-b-lg">
              <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
              
              {additionalDocuments.length > 0 && <div className="space-y-2">
                  <Label className="text-sm font-medium">Uploaded Documents ({additionalDocuments.length}):</Label>
                  {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-medium">{doc.file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({doc.extractedText.length} characters extracted)
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>}

        {/* Advanced Tailoring Options - For institution */}
        {tailoringSource === 'institution' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptionsInstitution} onOpenChange={setShowAdvancedOptionsInstitution}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer bg-white/90 rounded-t-2xl border-b border-gray-200">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your institutional document preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptionsInstitution ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[{
                            id: "feature-solution-asset-inst",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-institution-inst",
                            checked: mentionCompanyName,
                            onChange: setMentionCompanyName,
                            title: "Institution Name Integration",
                            description: "Naturally weave the target institution name into your professional summary"
                          }, {
                            id: "mention-mission-inst",
                            checked: mentionCompanyMission,
                            onChange: setMentionCompanyMission,
                            title: "Mission Alignment",
                            description: "Reference the institution's mission statement to show alignment with values"
                          }, {
                            id: "mention-education-inst",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials and academic achievements"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                          {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                              RECOMMENDED
                            </Badge>}
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience for institutional applications
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing experience structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={`method-inst-${method.value}`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={`method-inst-${method.value}`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'irrelevant-filtering-inst',
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Template Selection */}
                  

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-inst-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-inst-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition-inst',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian-inst',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements-inst',
                            label: 'Institutional Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to institutional values and goals.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={`${level.value}-inst`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${level.value}-inst`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={`${style.value}-inst`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${style.value}-inst`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}

        {/* Select Documents to Revise - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 3: Select Documents to Revise/Update</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Choose which professional documents you want to revise and update
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[{
                      value: 'resume',
                      label: 'Resume',
                      icon: FileText,
                      description: 'Revise and update your resume'
                    }, {
                      value: 'cover-letter',
                      label: 'Cover Letter',
                      icon: Mail,
                      description: 'Update your cover letter'
                    }, {
                      value: 'linkedin',
                      label: 'LinkedIn Profile',
                      icon: Linkedin,
                      description: 'Enhance your LinkedIn profile'
                    }, {
                      value: 'outreach-letter',
                      label: 'Outreach Letter',
                      icon: TrendingUp,
                      description: 'Improve your networking outreach letters'
                    }, {
                      value: 'thank-you-letter',
                      label: 'Thank You Letter',
                      icon: Heart,
                      description: 'Polish your thank you letters'
                    }, {
                      value: 'executive-bio',
                      label: 'Executive Bio',
                      icon: Users,
                      description: 'Enhance your executive biography'
                    }].map(doc => <div key={doc.value} className={`group p-4 rounded-xl border transition-all duration-300 cursor-pointer ${selectedDocuments.includes(doc.value) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`} onClick={() => handleDocumentToggle(doc.value)}>
                    <div className="flex items-start space-x-3">
                      <Checkbox id={doc.value} checked={selectedDocuments.includes(doc.value)} onChange={() => {}} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedDocuments.includes(doc.value) ? 'bg-green-100 text-green-600' : 'bg-muted group-hover:bg-primary/10'}`}>
                            <doc.icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{doc.label}</div>
                            <div className="text-xs text-muted-foreground">{doc.description}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Revisions Instructions - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 4: Upload Documents & Instructions</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Upload all files requiring updates/revisions along with any notes, reference materials, or additional instructions
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6 rounded-b-lg">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="revision-instructions" className="text-sm font-medium">
                    Detailed Revision Instructions
                  </Label>
                  <Textarea id="revision-instructions" placeholder="Provide comprehensive instructions for your document revisions. Include specific changes, preferences, target audience, tone requirements, and any other relevant details..." value={revisionInstructions} onChange={e => setRevisionInstructions(e.target.value)} rows={6} className="mt-1" />
                </div>
                
                <div className="border-t pt-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">
                      Upload All Relevant Files
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Include all documents needing updates, reference materials, style guides, examples, notes, and any additional content that will help with the revision process.
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <DocumentUpload onUploadComplete={(text, filename) => {
                          const file = new File([text], filename || 'revision-document.txt', {
                            type: 'text/plain'
                          });
                          setAdditionalDocuments(prev => [...prev, {
                            file,
                            extractedText: text
                          }]);
                        }} title="Upload Documents & Materials" description="Upload documents, notes, instructions, or reference materials (PDF, Word, TXT)" acceptedTypes={['.doc', '.docx', '.txt', '.pdf']} />
                  </div>
                  
                  {additionalDocuments.length > 0 && <div className="mt-4 space-y-2">
                      <Label className="text-sm font-medium">Uploaded Files:</Label>
                      <div className="space-y-2">
                        {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{doc.file.name}</span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setAdditionalDocuments(prev => prev.filter((_, i) => i !== index))} className="h-auto p-1 text-muted-foreground hover:text-destructive">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </CardContent>
          </Card>}

        {/* Preset Revision Options - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 5: Preset Revision Options</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Select common revision types to apply to your documents
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{
                      id: 'remove-redundancies',
                      emoji: '✂️',
                      label: 'Remove Redundancies'
                    }, {
                      id: 'change-designs',
                      emoji: '🎨',
                      label: 'Change Designs/Templates'
                    }, {
                      id: 'improve-formatting',
                      emoji: '📝',
                      label: 'Improve the Formatting'
                    }, {
                      id: 'remove-skills-gaps',
                      emoji: '🔧',
                      label: 'Remove Skills Gaps'
                    }, {
                      id: 'highlight-transferable',
                      emoji: '🌟',
                      label: 'Highlight Transferable Skills'
                    }, {
                      id: 'optimize-work-history',
                      emoji: '📈',
                      label: 'Optimize Work History'
                    }, {
                      id: 'add-metrics',
                      emoji: '📊',
                      label: 'Add Metrics & Numbers'
                    }, {
                      id: 'fact-check-content',
                      emoji: '🔍',
                      label: 'Fact Check Content for Accuracy'
                    }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 cursor-pointer ${selectedRevisionOptions.includes(option.id) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`} onClick={() => {
                      const newOptions = selectedRevisionOptions.includes(option.id) ? selectedRevisionOptions.filter(id => id !== option.id) : [...selectedRevisionOptions, option.id];
                      setSelectedRevisionOptions(newOptions);
                      // Mark step 4 as completed when at least one revision option is selected
                      setStep4Completed(newOptions.length > 0);
                    }}>
                    <div className="flex items-center space-x-3">
                      <Checkbox id={option.id} checked={selectedRevisionOptions.includes(option.id)} onChange={() => {}} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-lg">{option.emoji}</span>
                        <Label htmlFor={option.id} className="text-sm font-medium cursor-pointer group-hover:text-primary transition-colors">
                          {option.label}
                        </Label>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Submit Button */}
        <div className="flex justify-center pt-8 space-y-4">
          <div className="w-full max-w-2xl">
            {/* Progress Bar */}
            {isProcessing && <div className="mb-4 space-y-2 animate-fade-in">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Processing your resume...</span>
                  <span>{progressPercentage.toFixed(0)}%</span>
                </div>
                
                {/* Main Progress Bar - Wider and Green */}
                <div className="relative w-full h-4 bg-muted rounded-full overflow-hidden shadow-inner">
                  <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out shadow-sm ${isComplete || progressPercentage === 100 ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-green-500 to-green-400'}`} style={{
                        width: `${progressPercentage}%`
                      }} />
                  {/* Animated shine effect */}
                  <div className="absolute top-0 h-full w-8 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" style={{
                        left: `${Math.max(0, progressPercentage - 8)}%`,
                        opacity: progressPercentage > 5 ? 1 : 0
                      }} />
                  {/* Success checkmark when complete */}
                  {(isComplete || progressPercentage === 100) && <div className="absolute inset-0 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white animate-scale-in" />
                    </div>}
                </div>
                
                {/* Progress Steps */}
                <div className="flex justify-between text-xs text-muted-foreground/70 mt-1">
                  <span className={progressPercentage >= 15 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Analyzing
                  </span>
                  <span className={progressPercentage >= 40 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Tailoring
                  </span>
                  <span className={progressPercentage >= 70 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Optimizing
                  </span>
                  <span className={progressPercentage >= 85 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    {isComplete || progressPercentage === 100 ? "Complete!" : "Finalizing"}
                  </span>
                </div>
              </div>}

            {/* Final Tailoring Section */}
            <div className="mt-12 p-8 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 rounded-2xl shadow-lg border border-gray-200">
              <div className="text-center mb-8">
                
                <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-3">Ready to Start Tailoring?</h2>
                <p className="text-lg text-slate-600 dark:text-slate-300 max-w-lg mx-auto">
                  All steps completed! Start the AI-powered tailoring process now.
                </p>
              </div>

              {/* Generate Analysis Toggle - Moved to bottom */}
              <Card className={`shadow-lg border rounded-2xl mb-8 overflow-hidden ${generateAnalysis ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-primary-light/5 to-accent-light/8 border-primary/20'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Label htmlFor="generate-analysis-switch-bottom" className={`text-lg font-semibold flex items-center gap-3 ${generateAnalysis ? 'text-green-800' : 'text-slate-700'}`}>
                        {generateAnalysis && <span className="text-green-600 text-xl">✅</span>}
                        Generate Match Score & Analysis
                        {generateAnalysis && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1 rounded-full border-0">ENABLED</Badge>}
                      </Label>
                      <p className={`text-sm ${generateAnalysis ? 'text-green-700' : 'text-slate-500'}`}>
                        Compare BEFORE and AFTER resume match scores with detailed analysis
                      </p>
                    </div>
                    <Switch id="generate-analysis-switch-bottom" checked={generateAnalysis} onCheckedChange={setGenerateAnalysis} className={generateAnalysis ? 'data-[state=checked]:bg-green-600' : ''} />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center">
                <Button onClick={handleSubmit} size="lg" className="px-16 py-6 h-auto text-xl font-bold bg-gradient-to-r from-primary via-blue-600 to-indigo-600 hover:from-primary/90 hover:via-blue-600/90 hover:to-indigo-600/90 transition-all duration-300 shadow-2xl hover:shadow-primary/30 transform hover:scale-105 rounded-2xl relative overflow-hidden min-w-[320px] max-w-[420px] border-0" disabled={!step1Complete || !processedResumeData || !tailoringSource || isProcessing || tailoringSource === 'job-description' && selectedUploadMethod === 'paste' && !jobDescription || tailoringSource === 'job-description' && selectedUploadMethod === 'existing' && !selectedJobId || tailoringSource === 'job-position' && !jobPositionTitle || tailoringSource === 'institution' && !institutionName || tailoringSource === 'assessment-questions' && !assessmentQuestions.trim()}>
                  {isProcessing ? <div className="flex items-center">
                      <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mr-4" />
                      <span>Tailoring... {progressPercentage.toFixed(0)}%</span>
                    </div> : <div className="flex items-center">
                      <span className="mr-4 text-2xl">🚀</span>
                      <span>Start Tailoring</span>
                    </div>}
                </Button>
                
                {/* Cancel Button - Only show during processing */}
                {isProcessing && <Button onClick={cancelTailoring} size="lg" variant="outline" className="ml-6 h-auto px-10 py-6 text-lg font-semibold hover:bg-red-50 hover:text-red-600 border-2 border-red-300 text-red-600 transition-all duration-300 shadow-lg rounded-2xl">
                    <X className="w-5 h-5 mr-3" />
                    Cancel
                  </Button>}
              </div>
            </div>
          </div>
        </div>

        {/* Job Wizard Modal */}
        {showJobWizard && <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto p-4">
            <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full mx-auto my-4">
              <JobWizard onClose={() => setShowJobWizard(false)} onSave={savedJob => {
                    // Add the newly created job to the saved jobs list
                    setSavedJobs(prev => [savedJob, ...prev]);
                    // Select the newly created job and set job data ready
                    setSelectedJobId(savedJob.id);
                    // Construct job description from the saved job data
                    const jobDesc = `Company: ${savedJob.companyName}
Job Title: ${savedJob.jobTitle}
Company Mission: ${savedJob.companyMission}

Key Responsibilities:
${savedJob.selectedResponsibilities.map(resp => `• ${resp}`).join('\n')}

Requirements:
${savedJob.selectedRequirements.map(req => `• ${req}`).join('\n')}

Required Skills:
${savedJob.selectedSkills.map(skill => `• ${skill}`).join('\n')}`;
                    console.log('Debug - Setting job wizard data:', {
                      title: savedJob.jobTitle,
                      descLength: jobDesc.length
                    });
                    setProcessedJobData({
                      title: savedJob.jobTitle,
                      description: jobDesc
                    });
                    setJobDataReady(true);
                    // Switch to existing job mode and close wizard
                    setSelectedUploadMethod('existing');
                    setShowJobWizard(false);
                    // Show success toast
                    toast({
                      title: "Job Created Successfully!",
                      description: `${savedJob.jobTitle} at ${savedJob.companyName} has been created and selected.`
                    });
                  }} />
            </div>
          </div>}
            </div>
          </div>
        </div>
      </div>

    {/* Advanced Tailoring Wizard - 3 Steps */}
    {showAdvancedWizard && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        {wizardStep === 'responsibilities' && <Step1Responsibilities responsibilities={jobAnalysis?.responsibilities || []} selectedResponsibilities={selectedResponsibilities} onSelectionChange={setSelectedResponsibilities} onNext={() => setWizardStep('requirements')} onCancel={() => {
          setShowAdvancedWizard(false);
          setWizardStep('responsibilities');
        }} />}
        
        {wizardStep === 'requirements' && <Step2Requirements requirements={jobAnalysis?.requirements || []} selectedRequirements={selectedRequirements} onSelectionChange={setSelectedRequirements} onNext={() => setWizardStep('skills')} onBack={() => setWizardStep('responsibilities')} />}
        
        {wizardStep === 'skills' && <Step3Skills skills={jobAnalysis?.skills || []} selectedSkills={selectedSkills} onSelectionChange={setSelectedSkills} onSave={async () => {
          setSavingAnalysis(true);
          try {
            const savedAnalysis = await JobAnalysisService.saveJobAnalysis({
              jobTitle: jobPositionTitle,
              industry: jobPositionIndustry,
              selectedResponsibilities,
              selectedRequirements,
              selectedSkills,
              allResponsibilities: jobAnalysis?.responsibilities || [],
              allRequirements: jobAnalysis?.requirements || [],
              allSkills: jobAnalysis?.skills || []
            });

            // Automatically select the newly created analysis
            setSelectedSavedAnalysisId(savedAnalysis.id);
            setShowAdvancedWizard(false);
            setWizardStep('responsibilities');
            loadSavedJobAnalyses(); // Refresh the list
            toast({
              title: "Analysis saved and selected!",
              description: "Your advanced tailoring details are ready for use. You can now proceed with tailoring."
            });
          } catch (error) {
            toast({
              title: "Save failed",
              description: "Could not save the analysis. Please try again.",
              variant: "destructive"
            });
          } finally {
            setSavingAnalysis(false);
          }
        }} onBack={() => setWizardStep('requirements')} isSaving={savingAnalysis} />}
      </div>}

     {/* Debug Viewer - Hidden from users but kept for debugging */}
     {(debugData.length > 0 || true) && <div className="hidden p-6 mt-8 border-t bg-muted/30">
         <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
           <div className="flex items-center gap-2">
             <span className="text-yellow-600">⚠️</span>
             <p className="text-sm text-yellow-800">
               <strong>Debug Mode Active:</strong> Found {debugData.length} previous tailoring requests. 
               Check console for loading details. This data persists across page refreshs.
             </p>
           </div>
         </div>
         <TailoringDebugViewer debugData={debugData} />
        </div>}
      </div>
    </div>;
}

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(ResumeTailoringTest);